import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
  Suspense,
  startTransition,
} from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Spinner,
  Button,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tag,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";

// Context and Utils
import { UserContext } from "./UserContext";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { cleanJSONString } from "../utils/planDataUtils";
import { orderPlanNavigationFeatures } from "../utils/planNavigationOrder";

// Services
import workflowRegistry from "../services/WorkflowRegistry";
import ComponentActionHandler from "../services/ComponentActionHandler";
import chatStateManager from "../services/ChatStateManager";
import modularWorkflowEngine from "../services/ModularWorkflowEngine";
import { workflowLaunchCache } from "../services/WorkflowLaunchCache";
import { loadingPhrases } from "../config/loadingConfig";

// Components - Import with explicit checks
import NavBar from "./NavBar";

import {
  filterRelevantTools,
  calculateProgramLengthAndStages,
  determineToolDuration,
} from "./generateActivityPlan";
// sanitizeProgramsForSave, formatDuration moved to inline functions - no longer importing from planDataUtils
import {
  strategyButtonConfig,
  slickButtonConfig,
  stepByStepButtonConfig,
  calendarButtonConfig,
} from "../config/navigationConfig";

// Custom Hooks
import { usePlanDataLoader } from "../hooks/usePlanDataLoader";
import { usePlanState } from "../hooks/usePlanState";

// Components for Add View feature
import AddViewSelector from "./AddViewSelector";

// Fallback component for failed lazy imports
const LazyLoadFallback = ({ componentName, error }) => (
  <Box p={4} textAlign="center">
    <Text color="red.500">Failed to load {componentName}</Text>
    <Text fontSize="sm" color="gray.500">
      {error?.message}
    </Text>
  </Box>
);

// Loading fallback component for Suspense
const ViewLoadingFallback = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="400px"
    width="100%"
  >
    <VStack spacing={4}>
      <Spinner size="lg" color="blue.500" />
      <Text color="gray.600">Loading view...</Text>
    </VStack>
  </Box>
);

// Lazy load heavy components with comprehensive error handling and preloading
const CalendarView = React.lazy(() => import("./CalendarView"));
const DesignStrategyVision = React.lazy(() => import("./DesignStrategyVision"));
const StepByStepView = React.lazy(() => import("./StepByStepView"));
const SlickView = React.lazy(() => import("./SlickView"));
const NotionStyleSOW = React.lazy(() => import("./NotionStyleSOW"));
const ReadmeGenerator = React.lazy(() => import("./ReadmeGenerator"));
const CompetitorAnalysisView = React.lazy(
  () => import("./CompetitorAnalysisView"),
);
const ProductStrategyView = React.lazy(() => import("./ProductStrategyView"));
const DecisionsView = React.lazy(() => import("./DecisionsView"));
const PulseView = React.lazy(() => import("./PulseView"));
const DesignAuthorityView = React.lazy(() => import("./DesignAuthorityView"));

const ActivityDetailPanel = React.lazy(() => import("./ActivityDetailPanel"));

const AddProgramModal = React.lazy(() => import("./AddProgramModal"));

const ToolSelectionModal = React.lazy(() => import("./ToolSelectionModal"));

const EmbeddedChatPanel = React.lazy(() => import("./EmbeddedChatPanel"));
const SharePlanModal = React.lazy(() => import("./SharePlanModal"));
const McpModal = React.lazy(() => import("./McpModal"));
const LearningSidebar = React.lazy(() => import("./LearningSidebar"));
const JobsDrawer = React.lazy(() => import("./JobsDrawer"));
const InProductWalkthrough = React.lazy(() => import("./InProductWalkthrough"));

// CRITICAL FIX: Module-level guard survives component remounts
// This prevents workflow from re-triggering after navigate() causes remount
const globalWorkflowExecutionGuard = {
  executedWorkflows: new Map(), // planId_workflowId -> timestamp

  hasExecuted(planId, workflowId) {
    const key = `${planId}_${workflowId}`;
    return this.executedWorkflows.has(key);
  },

  markExecuted(planId, workflowId) {
    const key = `${planId}_${workflowId}`;
    this.executedWorkflows.set(key, Date.now());
    console.log(`🔒 Global guard: Marked ${key} as executed`);
  },

  clear(planId, workflowId) {
    const key = `${planId}_${workflowId}`;
    this.executedWorkflows.delete(key);
    console.log(`🧹 Global guard: Cleared ${key}`);
  },
};

const PlanPage = ({
  programsOfWork,
  methods,
  tools,
  deliverables,
  onChatCollapseChange,
}) => {
  const {
    setCurrentPlanId,
    currentPlanId,
    currentPlanDeleted,
    setCurrentPlanDeleted,
    userDetails,
  } = useContext(UserContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams(); // Get planId from URL
  const [searchParams] = useSearchParams();
  const toast = useToast();

  // Initialize all state early to prevent temporal dead zone errors
  // CRITICAL FIX: React strict mode causes double-mount which loses location.state
  // Solution: Initialize from cache OR location.state immediately
  const [cachedLaunchState, setCachedLaunchState] = useState(() => {
    const cacheKey = location.state?._cacheKey;
    if (cacheKey) {
      const cached = workflowLaunchCache.get(cacheKey);
      if (cached) {
        console.log("💾 Initialized cachedLaunchState from cache:", cached);
        return cached;
      }
    }
    // Fallback to location.state if no cache
    if (location.state?.initiateStrategyCreation) {
      console.log("💾 Initialized cachedLaunchState from location.state");
      return location.state;
    }
    return null;
  });
  const [isStrategyCreationMode, setIsStrategyCreationMode] = useState(() => {
    // CRITICAL FIX: Only use cache when cacheKey exists AND no planId in URL
    // This prevents saved plans from inheriting stale workflow triggers
    const cacheKey = location.state?._cacheKey;
    const urlHasPlanId =
      window.location.pathname.includes("/plan/") &&
      window.location.pathname.split("/plan/")[1];
    let checkState = location.state;

    // GUARD: If loading a saved plan (URL has planId), never use cache
    if (urlHasPlanId) {
      console.log(
        "🔍 isStrategyCreationMode INIT: Loading saved plan, skipping cache",
      );
      return false;
    }

    // Only use cache if we have a valid cacheKey (indicates new plan navigation)
    if (cacheKey) {
      const cached = workflowLaunchCache.get(cacheKey);
      if (cached) {
        console.log(
          "🔥 isStrategyCreationMode INIT: Using cached state via key",
          cached,
        );
        checkState = cached;
      }
    }

    const shouldInitiate = !!(
      checkState?.initiateStrategyCreation && checkState?.workflowType
    );

    console.log("🔥 isStrategyCreationMode INIT RESULT:", {
      workflow: checkState?.workflowType,
      initiateFlag: checkState?.initiateStrategyCreation,
      usedCache: !!cacheKey,
      urlHasPlanId,
      result: shouldInitiate ? "autonomous" : false,
    });

    return shouldInitiate ? "autonomous" : false;
  });
  const [isCreatingStrategy, setIsCreatingStrategy] = useState(false);
  const [hasStrategyBeenCreated, setHasStrategyBeenCreated] = useState(false);
  const [planLoadInitialized, setPlanLoadInitialized] = useState(false);
  const [isPlanReadyWalkthroughReady, setIsPlanReadyWalkthroughReady] =
    useState(false);

  // Agent-driven workflow trigger: when set to a featureId, that feature's workflow auto-triggers
  const [pendingFeatureWorkflowTrigger, setPendingFeatureWorkflowTrigger] =
    useState(null);

  // CRITICAL FIX: Prevent scroll chaining by controlling body overflow when PlanPage is mounted
  // This prevents scroll events from propagating to the document body when scrolling inside the plan view
  useEffect(() => {
    // Store original body overflow to restore on unmount
    const originalOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    // Set overflow hidden on body and html to prevent scroll chaining
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    console.log(
      "🔒 PlanPage mounted: Body scroll disabled to prevent scroll chaining",
    );

    return () => {
      // Restore original overflow on unmount
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      console.log("🔓 PlanPage unmounted: Body scroll restored");
    };
  }, []);

  useEffect(() => {
    setIsPlanReadyWalkthroughReady(false);
    if (!currentPlanId || isStrategyCreationMode || isCreatingStrategy) return;

    let cancelled = false;
    let attempts = 0;

    const checkPulseReady = async () => {
      attempts += 1;
      try {
        const response = await fetchWithAuth(`/api/pulse/${currentPlanId}/readiness`);
        if (!response?.ok) return;
        const data = await response.json();
        if (!cancelled && data?.ready) {
          setIsPlanReadyWalkthroughReady(true);
          return true;
        }
      } catch {
        // Walkthrough eligibility is non-critical.
      }
      return false;
    };

    checkPulseReady();
    const interval = setInterval(() => {
      if (cancelled || attempts >= 18) {
        clearInterval(interval);
        return;
      }
      checkPulseReady().then((ready) => {
        if (ready) clearInterval(interval);
      });
    }, 8000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentPlanId, isCreatingStrategy, isStrategyCreationMode]);

  // Set PABS default brand CSS variables so views using --brand-* tokens
  // (Brief, Product Strategy, Decisions, Competitor Analysis) look consistent
  // with the rest of the app. Customer brand overrides only apply in SharedPlanViewer.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-page-bg',             '#F7FAFC'); // gray.50
    root.style.setProperty('--brand-block-bg',            '#FFFFFF');
    root.style.setProperty('--brand-border',              '#E2E8F0'); // brand.200
    root.style.setProperty('--brand-heading',             '#2D3748'); // brand.700 — matches original brand.700 usage
    root.style.setProperty('--brand-text',                '#2D3748'); // brand.700
    root.style.setProperty('--brand-text-on-page',        '#2D3748'); // brand.700
    root.style.setProperty('--brand-text-on-block',       '#2D3748'); // brand.700 — matches original gray.700/gray.800 usage
    root.style.setProperty('--brand-text-muted',          '#718096'); // brand.500
    root.style.setProperty('--brand-text-on-block-muted', '#718096'); // brand.500 — matches original gray.500
    root.style.setProperty('--brand-primary',             '#718096'); // brand.500
    root.style.setProperty('--brand-primary-hover',       '#4A5568'); // brand.600
    root.style.setProperty('--brand-text-on-primary',     '#FFFFFF');
    root.style.setProperty('--brand-secondary',           '#4A5568'); // brand.600
    root.style.setProperty('--brand-secondary-hover',     '#2D3748'); // brand.700
    root.style.setProperty('--brand-accent-card-bg',      '#2D3748'); // brand.700
    root.style.setProperty('--brand-text-on-accent-card', '#FFFFFF');
    root.style.setProperty('--brand-text-on-primary-500', '#FFFFFF');
    root.style.setProperty('--brand-text-on-primary-600', '#FFFFFF');
    root.style.setProperty('--brand-text-on-primary-700', '#FFFFFF');
    root.style.setProperty('--brand-text-on-primary-800', '#FFFFFF');

    return () => {
      const VARS = [
        '--brand-page-bg', '--brand-block-bg', '--brand-border',
        '--brand-heading', '--brand-text', '--brand-text-on-page',
        '--brand-text-on-block', '--brand-text-muted', '--brand-text-on-block-muted',
        '--brand-primary', '--brand-primary-hover', '--brand-text-on-primary',
        '--brand-secondary', '--brand-secondary-hover',
        '--brand-accent-card-bg', '--brand-text-on-accent-card',
        '--brand-text-on-primary-500', '--brand-text-on-primary-600',
        '--brand-text-on-primary-700', '--brand-text-on-primary-800',
      ];
      VARS.forEach(v => root.style.removeProperty(v));
    };
  }, []);

  // Hydrate cached state AFTER mount (when Strict Mode is done remounting)
  // CRITICAL FIX: Run on each new navigation by watching location.state._cacheKey
  useEffect(() => {
    console.log(
      "🔥🔥🔥 CACHE HYDRATION USEEFFECT RUNNING - cacheKey:",
      location.state?._cacheKey,
    );

    // CRITICAL FIX: ALWAYS clear ChatStateManager for NEW plans (including autonomous)
    // Previous code incorrectly skipped reset for autonomous workflows, causing stale messages
    // GUARD: Only skip if plan has already been saved (currentPlanId exists)
    if (
      (location.state?.isNewPlan || location.state?.initiateStrategyCreation) &&
      !currentPlanId
    ) {
      const {
        default: chatStateManager,
      } = require("../services/ChatStateManager");
      // Use full reset for new plan creation to clear all stale progress UI messages
      chatStateManager.resetForNewPlanCreation();
      console.log("🧹 Full reset ChatStateManager for new plan creation");

      // CRITICAL FIX: Also clear stale sessionStorage workflow_initialized keys
      // These can block the workflow from starting if left from a previous test
      const cacheKey = location.state?._cacheKey;
      if (cacheKey) {
        const sessionKey = `workflow_initialized_${cacheKey}`;
        if (sessionStorage.getItem(sessionKey)) {
          sessionStorage.removeItem(sessionKey);
          console.log("🧹 Cleared stale sessionStorage key:", sessionKey);
        }
      }
    } else if (currentPlanId) {
      console.log(
        "⏭️ Skipping chat reset - plan already saved with ID:",
        currentPlanId,
      );
    }

    const cacheKey = location.state?._cacheKey;
    if (cacheKey) {
      const cached = workflowLaunchCache.get(cacheKey);
      if (cached) {
        console.log("✅ Hydrated cached launch state after mount:", cached);
        setCachedLaunchState(cached);

        // CRITICAL FIX: Restore isStrategyCreationMode from cache to survive Strict Mode remount
        if (cached.initiateStrategyCreation && cached.workflowType) {
          console.log(
            "🔥 RESTORING isStrategyCreationMode to 'autonomous' from cached state",
          );
          setIsStrategyCreationMode("autonomous");
          setCurrentWorkflowId(cached.workflowType);
        }

        // Clear cache after hydration
        workflowLaunchCache.clear(cacheKey);
        console.log("🧹 Cleared cache after hydration:", cacheKey);
      }
    }
  }, [location.state?._cacheKey]); // CRITICAL: Re-run on each new navigation with unique cacheKey

  // State to track if the current plan is new or existing
  // CRITICAL: Respect location.state?.isNewPlan as authoritative for new plan creation
  const [isNewPlan, setIsNewPlan] = useState(() => {
    if (location.state?.isNewPlan || location.state?.initiateStrategyCreation) {
      return true;
    }
    return !currentPlanId || !planId;
  });

  // Move all useRef hooks to the top before any conditional logic
  const isInitialLoad = useRef(isNewPlan);
  const saveTimeoutRef = useRef(null);
  const cancelRef = useRef();
  const strategyWorkflowService = useRef(null);
  const chatbotRef = useRef(null);
  const syncedPlanIdRef = useRef(null); // Track which plan ID we've synced features for

  // Loading phrases now imported from config
  const [strategyCreationPhrase, setStrategyCreationPhrase] = useState("");
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);

  // CRITICAL SECTION: State Management and Hooks
  // ==============================================

  // Initialize the plan state hook first
  const planStateHook = usePlanState(
    userDetails,
    currentPlanId,
    setCurrentPlanId,
  );

  // Extract all state values and setters from the hook
  const {
    currentDesignChallenge,
    setCurrentDesignChallenge,
    planName,
    setPlanName,
    shortSummary,
    setShortSummary,
    problemStatement,
    setProblemStatement,
    goalStatement,
    setGoalStatement,
    targetStatement,
    setTargetStatement,
    objectivesStatement,
    setObjectivesStatement,
    deliverablesStatement,
    setDeliverablesStatement,
    metricsStatement,
    setMetricsStatement,
    stakeholdersCustomersInvolvement,
    setStakeholdersCustomersInvolvement,
    experienceDesignPrinciples,
    setExperienceDesignPrinciples,
    selectedPrograms,
    setSelectedPrograms,
    finalProgramSequence,
    setFinalProgramSequence,
    totalDuration,
    setTotalDuration,
    enabledFeatures,
    setEnabledFeatures,
    currentWorkflowId: planStateWorkflowId,
    setCurrentWorkflowId: setPlanStateWorkflowId,
    isSaving, // setIsSaving - not used locally
    isSaved,
    setIsSaved,
    planState,
    setPlanState,
    savePlan,
  } = planStateHook;

  // Workflow-driven UI state
  // CRITICAL FIX: Initialize currentWorkflowId synchronously from navigation state
  // This ensures chat panel has workflow ID available immediately on mount
  const [currentWorkflowId, setCurrentWorkflowId] = useState(() => {
    return location.state?.workflowType || planStateWorkflowId || null;
  });
  const [uiConfig, setUIConfig] = useState(null);
  const VALID_VIEWS = new Set(["pulse", "strategy", "productStrategy", "competitor", "decisions", "readme", "stepByStep", "calendar", "slick", "designAuthority", "design_authority"]);
  // Track whether the initial view came from a URL param — used to block workflow default override
  const viewFromUrlRef = useRef(false);
  const [currentView, setCurrentView] = useState(() => {
    const v = searchParams.get("view");
    if (v && VALID_VIEWS.has(v)) {
      viewFromUrlRef.current = true;
      return v;
    }
    return "pulse";
  });
  const [activeDocumentGenerationId, setActiveDocumentGenerationId] = useState(null);

  useEffect(() => {
    if (!currentPlanId) return;
    const storedId = window.sessionStorage.getItem(`pabs:documentGeneration:${currentPlanId}`);
    if (!storedId) return;
    setActiveDocumentGenerationId(Number(storedId));
    viewFromUrlRef.current = true;
    setCurrentView('readme');
  }, [currentPlanId]);

  useEffect(() => {
    const handleOpenDocument = event => {
      const documentId = Number(event.detail?.documentId);
      if (!documentId) return;
      setActiveDocumentGenerationId(documentId);
      viewFromUrlRef.current = true;
      setCurrentView('readme');
    };
    window.addEventListener('pabs:openDocument', handleOpenDocument);
    return () => window.removeEventListener('pabs:openDocument', handleOpenDocument);
  }, []);

  useEffect(() => {
    const handleDocumentGeneration = event => {
      const detail = event.detail || {};
      if (!detail.document_id) return;
      if (detail.planId && Number(detail.planId) !== Number(currentPlanId)) return;
      setActiveDocumentGenerationId(Number(detail.document_id));
      if (detail.eventType === 'document_generation_started') {
        window.sessionStorage.setItem(
          `pabs:documentGeneration:${currentPlanId}`,
          String(detail.document_id),
        );
        viewFromUrlRef.current = true;
        setCurrentView('readme');
      } else if (['document_generation_completed', 'document_generation_failed'].includes(detail.eventType)) {
        window.sessionStorage.removeItem(`pabs:documentGeneration:${currentPlanId}`);
      }
    };
    window.addEventListener('pabs:documentGeneration', handleDocumentGeneration);
    return () => window.removeEventListener('pabs:documentGeneration', handleDocumentGeneration);
  }, [currentPlanId]);

  // Consume ?view= param — works for both hard load and SPA navigation (RouterLink clicks).
  // Must set viewFromUrlRef here too (not just in the useState initializer above) — this
  // navigation bumps location.key, which re-runs initializeWorkflow's defaultView effect.
  // Without this, that effect finds a stale `false` ref and immediately clobbers the view
  // this very click just set, so in-chat "Go to the X tab" links silently no-op post-mount.
  useEffect(() => {
    const v = searchParams.get("view");
    const documentId = Number(searchParams.get("document"));
    if (v && VALID_VIEWS.has(v)) {
      viewFromUrlRef.current = true;
      setCurrentView(v);
      if (v === 'readme' && documentId) setActiveDocumentGenerationId(documentId);
      navigate({pathname:location.pathname,hash:location.hash}, { replace: true });
    } else if (v) {
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const jobId = Number(searchParams.get("job"));
    if (!jobId) return;
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("pabs:openJobs", { detail: { taskId: jobId } }));
    }, 0);
    navigate(location.pathname, { replace: true });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // A client links the person to a Learning that waits for their review.
  useEffect(() => {
    const learningId = Number(searchParams.get("learning"));
    if (!learningId) return;
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("pabs:openLearning", { detail: { learningId } }));
    }, 0);
    navigate(location.pathname, { replace: true });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const [navigationContext, setNavigationContext] = useState(null);
  const [executionPlan, setExecutionPlan] = useState(null);
  const [strategyVersion, setStrategyVersion] = useState(0);
  const [strategyLifecycleStatus, setStrategyLifecycleStatus] = useState("exploring");
  // null = nothing applied; { learning: { id, learning, decided_at }, changes: [...] }
  const [recentlyApplied, setRecentlyApplied] = useState(null);
  const [strategyResetKey, setStrategyResetKey] = useState(0);
  const [fieldUpdates, setFieldUpdates] = useState({});

  // CRITICAL FIX: Track chat initialization completion
  const [isChatInitialized, setIsChatInitialized] = useState(false);
  // True from the moment a plan created in this page opens on its saved URL,
  // until the person switches to another plan.
  const creationChatHandoffRef = useRef(false);

  // CRITICAL FIX: Determine if this is a NEW plan launch vs SAVED plan load
  // NEW PLAN: location.state.initiateStrategyCreation or _cacheKey exists
  // SAVED PLAN: planId exists, no launch signal in navigation state
  // This flag should ONLY be true for new autonomous workflows, never for saved plans
  const shouldAutoLaunchWorkflow = React.useMemo(() => {
    const hasLaunchSignal = !!(
      location.state?.initiateStrategyCreation || location.state?._cacheKey
    );
    const isSavedPlanLoad = !!planId && !hasLaunchSignal;

    console.log("🚦 shouldAutoLaunchWorkflow calculation:", {
      hasLaunchSignal,
      isSavedPlanLoad,
      planId,
      initiateStrategyCreation: location.state?.initiateStrategyCreation,
      _cacheKey: location.state?._cacheKey,
      result: hasLaunchSignal && !isSavedPlanLoad,
    });

    return hasLaunchSignal && !isSavedPlanLoad;
  }, [
    planId,
    location.state?.initiateStrategyCreation,
    location.state?._cacheKey,
  ]);

  // CRITICAL FIX: Clear currentPlanId for new plan creation
  // This MUST run before workflow initialization to prevent stale plan context
  React.useEffect(() => {
    if (location.state?.initiateStrategyCreation && location.state?.isNewPlan) {
      console.log("🆕 CLEARING stale currentPlanId for new plan creation");
      setCurrentPlanId(null);
    }
  }, [
    location.state?.initiateStrategyCreation,
    location.state?.isNewPlan,
    setCurrentPlanId,
  ]);

  // CRITICAL FIX: Chat loading gate - only render chat when plan context AND chat are ready
  // This prevents chat from initializing with stale/null planId
  const isChatReady = React.useMemo(() => {
    // CRITICAL: currentWorkflowId MUST be set for both new AND saved plans
    // Without it, workflow execution will fail
    if (!currentWorkflowId) {
      console.log("🚪 Chat Loading Gate: BLOCKED - currentWorkflowId not set");
      return false;
    }

    // For NEW plans (strategy creation): ready when initiating creation AND chat initialized
    const isNewPlanCreation =
      location.state?.initiateStrategyCreation || (!planId && !currentPlanId);

    // For SAVED plans: ready when plan data is loaded, IDs match, AND chat initialized
    const isSamePlan = !!planId && String(currentPlanId ?? "") === String(planId);
    const isSavedPlanReady =
      isSamePlan &&
      planLoadInitialized &&
      isChatInitialized;

    // The chat that just created this plan stays mounted on the saved plan's
    // URL while its conversation is linked, instead of unmounting and reloading.
    const isCreationHandoff = creationChatHandoffRef.current && isSamePlan;

    const ready = isNewPlanCreation || isCreationHandoff || isSavedPlanReady;

    console.log("🚪 Chat Loading Gate:", {
      ready,
      isNewPlanCreation,
      isSavedPlanReady,
      planId,
      currentPlanId,
      planLoadInitialized,
      currentWorkflowId,
      isChatInitialized,
      initiateStrategyCreation: location.state?.initiateStrategyCreation,
    });

    return ready;
  }, [
    planId,
    currentPlanId,
    planLoadInitialized,
    currentWorkflowId,
    isChatInitialized,
    location.state?.initiateStrategyCreation,
  ]);

  const refreshPlanContent = useCallback(async () => {
    if (!currentPlanId) return;
    try {
      const res = await fetchWithAuth(`/api/plans/${currentPlanId}`);
      const data = await res.json();
      if (data.short_summary != null) setShortSummary(data.short_summary);
      if (data.goal_statement != null) setGoalStatement(data.goal_statement);
      if (data.problem_statement != null) setProblemStatement(data.problem_statement);
      if (data.target_statement != null) setTargetStatement(data.target_statement);
      if (data.objectives_statement != null) {
        let obj = data.objectives_statement;
        for (let i = 0; i < 5 && typeof obj === 'string'; i++) {
          try { obj = JSON.parse(obj); } catch { obj = obj ? [obj] : []; break; }
        }
        if (Array.isArray(obj) && obj.length === 1 && typeof obj[0] === 'string' && obj[0].trim().startsWith('[')) {
          try { obj = JSON.parse(obj[0]); } catch { /* keep current value */ }
        }
        setObjectivesStatement(Array.isArray(obj) ? obj : obj ? [String(obj)] : []);
      }

      const parsePrograms = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value !== "string") return [];
        try {
          const parsed = JSON.parse(cleanJSONString(value));
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      };

      const finalPrograms = parsePrograms(data.final_program_sequence);
      const selectedPlanPrograms = parsePrograms(data.selected_programs);
      const refreshedPrograms = finalPrograms.length > 0 ? finalPrograms : selectedPlanPrograms;

      if (refreshedPrograms.length > 0) {
        setFinalProgramSequence(refreshedPrograms);
        setSelectedPrograms(refreshedPrograms);
        setTotalDuration(
          refreshedPrograms.reduce((sum, program) => {
            if (program.isIncluded === false) return sum;
            return sum + (Number(program.totalProgramLength ?? program.duration) || 0);
          }, 0)
        );
        setIsSaved(true);
      }
    } catch (err) {
      console.error("Failed to refresh plan content:", err);
    }
  }, [
    currentPlanId,
    setFinalProgramSequence,
    setSelectedPrograms,
    setTotalDuration,
    setIsSaved,
    setShortSummary,
    setGoalStatement,
    setProblemStatement,
    setTargetStatement,
    setObjectivesStatement,
  ]);

  // Standalone brief regeneration is disabled for existing plans. Briefs are
  // created by create_plan and edited through targeted update tasks only.
  useEffect(() => {
    const handler = async (e) => {
      if (e.detail?.capability !== 'generate_brief') return;
      console.warn('[PlanPage] Ignoring generate_brief completion for existing plan. Use targeted brief update tasks instead.');
    };

    window.addEventListener('pabs:generationComplete', handler);
    return () => window.removeEventListener('pabs:generationComplete', handler);
  }, []);

  // Refresh plan data when a task (create_agent_task with update_* capability) completes via Apply or execute_now
  useEffect(() => {
    const handler = () => refreshPlanContent();
    window.addEventListener('pabs-plan-updated', handler);
    return () => window.removeEventListener('pabs-plan-updated', handler);
  }, [refreshPlanContent]);

  // Poll only incremental background mission messages. The endpoint extracts the
  // small delta in Postgres so the complete conversation never crosses the Neon
  // proxy on every poll.
  useEffect(() => {
    const id = currentPlanId || planId;
    if (!id) return;
    const seen = new Set();
    let cursor = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    let conversationVersion = '';

    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await fetchWithAuth(
          `/api/plans/${id}/mission-messages?after=${encodeURIComponent(cursor)}&version=${encodeURIComponent(conversationVersion)}`
        );
        if (!res?.ok) return;
        const data = await res.json();
        if (data?.conversationUpdatedAt) conversationVersion = data.conversationUpdatedAt;
        const missionMessages = Array.isArray(data?.messages) ? data.messages : [];
        const unseen = missionMessages.filter(m => m.timestamp && m.text && !seen.has(m.timestamp));
        unseen.forEach(m => {
          seen.add(m.timestamp);
          if (m.timestamp > cursor) cursor = m.timestamp;
          chatbotRef.current?.addMessage?.({
            sender: 'bot',
            message: m.text || '',
            text: m.text || '',
            source: 'agent_mission',
            mission_id: m.mission_id,
            agent_run_id: m.agent_run_id,
            task_id: m.task_id,
            timestamp: m.timestamp,
          });
        });
      } catch {
        // non-critical
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentPlanId, planId]);

  const fetchFieldUpdates = useCallback(async () => {
    const id = currentPlanId || planId;
    if (!id) return;
    try {
      const res = await fetchWithAuth(`/api/learnings/${id}/field-updates`);
      if (res?.ok) {
        const data = await res.json();
        setFieldUpdates(data);
      }
    } catch {
      // non-critical — badges just won't show on this load
    }
  }, [currentPlanId, planId]);

  useEffect(() => {
    fetchFieldUpdates();
  }, [fetchFieldUpdates]);

  const fetchLearningsPendingCount = useCallback(async () => {
    if (!currentPlanId) return;
    try {
      const res = await fetchWithAuth(`/api/learnings/${currentPlanId}/pending-count`);
      if (res?.ok) {
        const data = await res.json();
        setLearningsPendingCount(Number(data?.count || 0));
      }
    } catch { /* non-critical */ }
  }, [currentPlanId]);

  useEffect(() => {
    if (!currentPlanId) return;
    fetchLearningsPendingCount();
    const interval = setInterval(fetchLearningsPendingCount, 30000);
    return () => clearInterval(interval);
  }, [currentPlanId, fetchLearningsPendingCount]);

  const handleStrategyLifecycleStatusChange = useCallback(async (status, source = "manual") => {
    if (!currentPlanId) return { ok: false };
    const previousStatus = strategyLifecycleStatus;
    setStrategyLifecycleStatus(status);
    try {
      const res = await fetchWithAuth(`/api/plans/${currentPlanId}/lifecycle-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, source }),
      });
      if (!res?.ok) throw new Error("Status update failed");
      const data = await res.json();
      setStrategyLifecycleStatus(data.status || status);
      toast({
        title: `Strategy marked as ${data.statusLabel || status}`,
        status: "success",
        duration: 2200,
        isClosable: true,
      });
      return { ok: true, status: data.status || status, statusLabel: data.statusLabel };
    } catch (err) {
      console.error("Failed to update strategy lifecycle status:", err);
      setStrategyLifecycleStatus(previousStatus);
      toast({
        title: "Couldn't update strategy status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return { ok: false };
    }
  }, [currentPlanId, strategyLifecycleStatus, toast]);

  // State for draggable panel sizing
  const [leftPanelWidth, setLeftPanelWidth] = useState(415); // Default 500px
  const [isDragging, setIsDragging] = useState(false);

  // State for dynamic navigation from Core Features Registry
  const [dynamicNavButtons, setDynamicNavButtons] = useState([]);

  // State for "Add New View" feature
  const [showAddView, setShowAddView] = useState(false);
  const [allFeaturesEnabled, setAllFeaturesEnabled] = useState(false);

  // State and refs for sticky tabs detection
  const [isTabsStuck, setIsTabsStuck] = useState(false);
  const tabsSentinelRef = useRef(null);
  const rightPanelScrollRef = useRef(null);

  // Floating bottom nav
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowBtnRef = useRef(null);
  // Bottom nav expand-on-hover/scroll temporarily disabled — always collapsed
  const [isNavShrunk, setIsNavShrunk] = useState(true);
  const scrollListenerCleanupRef = useRef(null);
  const setScrollContainerRef = useCallback((el) => {
    // Clean up previous listener if ref changes
    if (scrollListenerCleanupRef.current) {
      scrollListenerCleanupRef.current();
      scrollListenerCleanupRef.current = null;
    }
    rightPanelScrollRef.current = el;
  }, []);
  // base=mobile, sm=large phone, md=tablet, lg=desktop
  const maxInline = useBreakpointValue({ base: 2, sm: 3, md: 4, lg: 99 }) ?? 99;


  // Function to determine if current view should show duration tag
  const getCurrentViewDurationSetting = useCallback(() => {
    // First try to get setting from dynamic nav buttons (Core Features Registry)
    const currentViewConfig = dynamicNavButtons.find(
      (btn) => btn.config.id === currentView,
    );

    // If the view config has an explicit showDuration setting, use it
    if (currentViewConfig?.config?.showDuration !== undefined) {
      return currentViewConfig.config.showDuration;
    }

    // If the feature info has showDuration setting, use it
    if (currentViewConfig?.featureInfo?.showDuration !== undefined) {
      return currentViewConfig.featureInfo.showDuration;
    }

    // Fallback to reasonable defaults based on view type
    const viewDefaults = {
      strategy: false, // Design Strategy - concept-focused, hide duration
      stepByStep: true, // Action Plan - timeline-focused, show duration
      calendar: true, // Calendar - time-based view, show duration
      slick: true, // Overview - summary view, show duration
      sow: false, // SOW - document-focused, hide duration
      readme: false, // Readme - document-focused, hide duration
    };

    // Use view-specific default, or global setting, or true as final fallback
    return (
      viewDefaults[currentView] ?? uiConfig?.navbar?.showDurationTag ?? true
    );
  }, [currentView, dynamicNavButtons, uiConfig?.navbar?.showDurationTag]);

  // Modal states
  const {
    isOpen: isDeleteAlertOpen,
    // onOpen: onDeleteAlertOpen, // Not used
    onClose: onDeleteAlertClose,
  } = useDisclosure();
  const [programToDelete, setProgramToDelete] = useState(null);
  
  // Share modal state
  const {
    isOpen: isShareModalOpen,
    onOpen: onShareModalOpen,
    onClose: onShareModalClose,
  } = useDisclosure();

  // MCP modal state
  const {
    isOpen: isMcpModalOpen,
    onOpen: onMcpModalOpen,
    onClose: onMcpModalClose,
  } = useDisclosure();
  const [mcpInitialTool, setMcpInitialTool] = useState(null);
  const openMcpModal = React.useCallback((tool = null) => {
    setMcpInitialTool(tool || null);
    onMcpModalOpen();
  }, [onMcpModalOpen]);

  // Lets components that aren't direct ancestors of this modal (e.g. the Jobs
  // panel) open it too, mirroring the pabs:openGitHubRepositories pattern.
  useEffect(() => {
    const handleOpenMcpModal = () => openMcpModal();
    window.addEventListener('pabs:openMcpModal', handleOpenMcpModal);
    return () => window.removeEventListener('pabs:openMcpModal', handleOpenMcpModal);
  }, [openMcpModal]);


  // Jobs modal (Workflows tab) — triggered from Pulse settings links
  // Learnings sidebar state
  const {
    isOpen: isLearningsSidebarOpen,
    onOpen: onLearningsSidebarOpen,
    onClose: onLearningsSidebarClose,
  } = useDisclosure();
  const [learningsPendingCount, setLearningsPendingCount] = useState(0);
  const [requestedLearningId, setRequestedLearningId] = useState(null);
  useEffect(() => {
    const openLearning = event => {
      if (event.detail?.planId && String(event.detail.planId) !== String(currentPlanId)) return;
      const id = Number(event.detail?.learningId);
      if (!Number.isSafeInteger(id) || id <= 0) return;
      setRequestedLearningId(id); onLearningsSidebarOpen();
    };
    window.addEventListener('pabs:openLearning', openLearning);
    return () => window.removeEventListener('pabs:openLearning', openLearning);
  }, [currentPlanId, onLearningsSidebarOpen]);

  // State and functions for Add Program Modal - use programsOfWork directly, no filtering
  const availablePrograms = programsOfWork || [];
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [insertPosition, setInsertPosition] = useState(null);

  // Additional state that needs to be declared early
  const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false);
  const [isToolSelectionModalOpen, setIsToolSelectionModalOpen] =
    useState(false);
  const [selectedProgramForTools, setSelectedProgramForTools] = useState(null);
  const [isLoading] = useState(false); // setIsLoading not used locally

  // Use context state for chat collapse instead of local state
  const { isChatCollapsed, setIsChatCollapsed } = useContext(UserContext);
  const isNarrativeCollapsed = isChatCollapsed;
  const setIsNarrativeCollapsed = setIsChatCollapsed;

  const isMobile = useBreakpointValue({ base: true, md: false });

  // Track window width so effectiveMaxInline is based on available space, not the nav itself
  // (measuring the nav would create a circular dependency that traps tab count when shrinking)
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const contentPanelWidth = isMobile !== false
    ? windowWidth
    : isNarrativeCollapsed
      ? windowWidth
      : windowWidth - leftPanelWidth;

  // Effective inline limit — derived from available right-panel space, independent of nav's own width
  const effectiveMaxInline = useMemo(() => {
    const chatPx = (isMobile !== false || isNarrativeCollapsed) ? 0 : leftPanelWidth;
    const availableWidth = isMobile !== false
      ? windowWidth - 32
      : windowWidth - (chatPx + 32);
    const FIXED_W = 0;
    // Conservative tab width accounting for longer labels like "Competitor Analysis"
    const TAB_W = 130;
    // "..." overflow button width
    const DOTS_W = 52;
    const fromWidth = Math.max(2, Math.floor((availableWidth - FIXED_W - DOTS_W) / TAB_W));
    return Math.min(maxInline, fromWidth);
  }, [windowWidth, isMobile, isNarrativeCollapsed, leftPanelWidth, maxInline]);

  // On mobile, start with chat collapsed; on desktop always start open.
  // Guard against undefined (Chakra resolves breakpoints asynchronously on first render).
  useEffect(() => {
    if (isMobile === undefined) return;
    setIsChatCollapsed(isMobile); // true=collapsed on mobile, false=open on desktop
  }, [isMobile, setIsChatCollapsed]);

  const [loadingState, setLoadingState] = useState({});
  const [isActivityDetailPanelOpen, setIsActivityDetailPanelOpen] =
    useState(false);
  const [selectedActivityData, setSelectedActivityData] = useState(null);

  // Helper function to open/close Add Program Modal
  const onAddProgramModalOpen = () => setIsAddProgramModalOpen(true);
  const onAddProgramModalClose = () => setIsAddProgramModalOpen(false);

  // Debug logging for view state - moved after dynamicNavButtons declaration
  useEffect(() => {
    console.log("Current view changed to:", currentView);
    console.log(
      "Available navigation buttons:",
      dynamicNavButtons.map((btn) => btn.viewKey),
    );
  }, [currentView, dynamicNavButtons]);

  // Sync planId from URL with context
  useEffect(() => {
    // The URL gives a string and a fresh save gives a number: the same plan is
    // not a plan switch, and resetting here would reload the chat.
    if (planId && String(planId) !== String(currentPlanId ?? "")) {
      console.log("Plan ID changing from", currentPlanId, "to", planId);

      // Check if this is just a URL sync or actual plan switch
      const lastSyncTime = window[`planSync_${planId}`] || 0;
      const timeSinceSync = Date.now() - lastSyncTime;

      if (timeSinceSync < 5000) {
        console.log(
          "Skipping plan sync - recent sync detected, time since:",
          timeSinceSync,
          "ms",
        );
        return;
      }

      // Set sync timestamp
      window[`planSync_${planId}`] = Date.now();
      creationChatHandoffRef.current = false;

      // CRITICAL: Immediately disable auto-save for plan switch
      isInitialLoad.current = true;
      setPlanLoadInitialized(false);
      setIsSaved(true); // Prevent auto-save during switch

      // CRITICAL FIX: Reset chat state when switching plans to prevent loading wrong conversations
      console.log("🔄 Resetting chat state for plan switch");
      chatStateManager.resetForPlanSwitch();
      setIsChatInitialized(false); // Mark chat as not initialized

      // CRITICAL: Re-initialize chat with new plan's conversation and AWAIT completion
      const initNewPlanChat = async () => {
        console.log("🔄 Re-initializing chat for new plan:", planId);
        await chatStateManager.initializeForPlan(planId);
        console.log("✅ Chat re-initialized for plan:", planId);
        setIsChatInitialized(true); // Mark chat as initialized — auto-load handled by dedicated useEffect
      };
      initNewPlanChat();

      setCurrentPlanId(planId);

      // Set a longer timeout to re-enable auto-save after plan switch completes
      setTimeout(() => {
        console.log("🔄 Re-enabling auto-save after plan switch");
        isInitialLoad.current = false;
        setPlanLoadInitialized(true);
        console.log("✅ Auto-save re-enabled after plan switch");
      }, 6000); // Increased to 6 seconds to ensure plan switch completes
    }
  }, [planId, currentPlanId, setCurrentPlanId]);

  // CRITICAL FIX: Cache location.state early to survive React Strict Mode remounts
  // React Strict Mode wipes location.state on remount, making workflowType undefined
  // This ref captures the initial value and persists across remounts
  const navigationStateCache = React.useRef({
    workflowType: null,
    initiateStrategyCreation: null,
    designChallenge: null,
    cached: false,
  });

  // Hydrate cache on first mount (before Strict Mode wipes location.state)
  if (!navigationStateCache.current.cached && location.state?.workflowType) {
    navigationStateCache.current = {
      workflowType: location.state.workflowType,
      initiateStrategyCreation: location.state.initiateStrategyCreation,
      designChallenge: location.state.designChallenge,
      cached: true,
    };
    console.log(
      "💾 Cached launch state before Strict Mode wipe:",
      navigationStateCache.current,
    );
    // Cache cleared in useEffect after workflow consumes it
  }

  // Workflow detection and UI configuration using ProductWorkflowRegistry
  const [workflowExecutionGuard, setWorkflowExecutionGuard] = useState(null);

  useEffect(() => {
    const initializeWorkflow = async () => {
      console.error("🔥🔥🔥 WORKFLOW INITIALIZATION USEEFFECT RUNNING!");

      const urlHasPlanId =
        window.location.pathname.includes("/plan/") &&
        window.location.pathname.split("/plan/")[1];
      const hasExistingPlanContext = currentPlanId || urlHasPlanId;

      // Determine product/workflow ID - Priority: cached > location.state > loaded from DB
      // CRITICAL: Use cached value first (survives Strict Mode) then fall back to location.state then DB
      const productId =
        navigationStateCache.current.workflowType ||
        location.state?.workflowType ||
        planStateWorkflowId;

      // Wait for productId to be available before proceeding
      if (!productId) {
        console.log(
          "⏳ Waiting for workflow ID (from navigation or database)...",
        );
        return;
      }

      // CRITICAL FIX: Check global guard FIRST (survives remounts)
      // Use STABLE cacheKey that won't change when planId is set
      // This prevents re-triggering when currentPlanId changes from undefined → 37
      const guardKey = location.state?._cacheKey || currentPlanId || "pending";
      if (globalWorkflowExecutionGuard.hasExecuted(guardKey, productId)) {
        console.log(
          "🔒 Global guard: Workflow already executed for",
          guardKey,
          productId,
        );
        return;
      }

      // CRITICAL FIX: Include _cacheKey in fingerprint to force reset on each new navigation
      // This ensures Decision Call navigations don't get blocked by the guard
      // Fallback to location.key (unique per navigation) if _cacheKey is missing
      const cacheKey =
        location.state?._cacheKey || location.key || "no-cache-key";
      const executionFingerprint = `${cacheKey}_${currentPlanId}_${!!location.state?.initiateStrategyCreation}_${productId}`;

      console.log("🔍 GUARD CHECK:", {
        workflowExecutionGuard,
        executionFingerprint,
        cacheKey,
        globalGuardKey: guardKey,
        willReturn: workflowExecutionGuard === executionFingerprint,
      });

      // Skip if same execution fingerprint (prevents duplicate runs within same mount)
      if (workflowExecutionGuard === executionFingerprint) {
        console.log("⏭️ SKIPPING - guard matches fingerprint");
        return;
      }

      console.log("🚨 PLANPAGE INITIALIZATION:", {
        productId,
        fromNavigation: !!location.state?.workflowType,
        fromSavedPlan: !!planStateWorkflowId,
        hasInitiateFlag: !!location.state?.initiateStrategyCreation,
        hasDesignChallenge: !!location.state?.designChallenge,
        hasExistingPlanContext,
        currentPlanId,
        urlHasPlanId,
        hasStrategyBeenCreated,
        executionFingerprint,
        previousGuard: workflowExecutionGuard,
      });

      // Wait for productId to be available before proceeding
      if (!productId) {
        console.log(
          "⏳ Waiting for workflow ID (from navigation or database)...",
        );
        return;
      }

      // Set guard AFTER confirming we have productId
      setWorkflowExecutionGuard(executionFingerprint);

      // SIMPLE FIX: Get workflow from WorkflowRegistry and use its config
      const workflow = workflowRegistry.getWorkflow(productId);

      if (!workflow) {
        console.warn(
          `⚠️ Workflow "${productId}" not found in registry, using fallback`,
        );
        return;
      }

      console.log("📦 Workflow from registry:", {
        id: workflow.id,
        name: workflow.name,
        autonomousMode: workflow.uiComponents?.chatPanel?.autonomousMode,
        enabledFeatures: workflow.uiComponents?.enabledFeatures,
      });

      // Set workflow ID and UI config from registry
      setCurrentWorkflowId(workflow.id);
      // FIX #32: Only set default features for NEW plans, not for existing saved plans
      // For saved plans, usePlanDataLoader will set the correct enabledFeatures from database
      const isLoadingExistingPlan =
        currentPlanId && !location.state?.initiateStrategyCreation;
      setUIConfig((prev) => {
        const newConfig = { ...workflow.uiComponents };
        // If loading an existing plan and we already have features (from database), keep them
        // Otherwise use workflow defaults for new plans
        if (
          isLoadingExistingPlan &&
          prev?.enabledFeatures?.length >
            workflow.uiComponents?.enabledFeatures?.length
        ) {
          newConfig.enabledFeatures = prev.enabledFeatures;
          console.log(
            "📦 Preserving database enabledFeatures (more features than defaults):",
            prev.enabledFeatures,
          );
        }
        // FIX #36: ALWAYS merge views for existing plans - don't just compare lengths
        // This ensures views added from database are never lost when workflow re-initializes
        if (isLoadingExistingPlan && prev?.views?.length > 0) {
          const defaultViews = workflow.uiComponents?.views || [];
          const prevViews = prev.views || [];
          const mergedViews = [...new Set([...defaultViews, ...prevViews])];
          newConfig.views = mergedViews;
          console.log("📦 FIX #36: Merging views for existing plan:", {
            defaultViews,
            prevViews,
            mergedViews,
          });
        }
        return newConfig;
      });

      // CRITICAL FIX: Calculate execution plan using ModularWorkflowEngine
      // This determines which workflow steps to run (2 for quick_plan, 4 for full)
      try {
        const workflowConfig = await modularWorkflowEngine.createWorkflowConfig(
          workflow.id,
          workflowRegistry,
        );
        console.log("📊 Execution Plan Created:", {
          workflowId: workflow.id,
          steps: workflowConfig.executionPlan?.steps,
          skipSteps: workflowConfig.executionPlan?.skipSteps,
          estimatedDuration: workflowConfig.estimatedDuration,
        });
        setExecutionPlan(workflowConfig.executionPlan);
      } catch (error) {
        console.error("❌ Failed to create execution plan:", error);
        // Set null execution plan on error - workflow will use defaults
        setExecutionPlan(null);
      }

      // CRITICAL: Only set planStateWorkflowId if it's a NEW plan from navigation
      // DON'T overwrite for saved plans - they already have their workflow_id from database
      if (!planStateWorkflowId && location.state?.workflowType) {
        setPlanStateWorkflowId(location.state.workflowType);
      }

      // FIX #34: Only set default enabled features for NEW plans
      // For saved plans, the database features will be loaded by usePlanDataLoader (FIX #32)
      // This prevents workflow defaults from overwriting database features
      if (workflow.uiComponents?.enabledFeatures && !isLoadingExistingPlan) {
        setEnabledFeatures(workflow.uiComponents.enabledFeatures);
        console.log(
          `📊 Setting default features for NEW plan ${workflow.id}:`,
          workflow.uiComponents.enabledFeatures,
        );
      } else if (isLoadingExistingPlan) {
        console.log(
          `📊 FIX #34: Skipping default features for SAVED plan - will use database features`,
        );
      }

      // SIMPLIFIED LOGIC: Determine if this is a new strategy creation OR existing plan
      // CRITICAL FIX: Trust cached initiateStrategyCreation as authoritative (survives Strict Mode)
      // IGNORE hasStrategyBeenCreated when initiateStrategyCreation flag is present
      // The cached flag is the source of truth, not potentially-wiped location.state
      const isNewStrategyCreation =
        (navigationStateCache.current.initiateStrategyCreation ||
          location.state?.initiateStrategyCreation) &&
        (navigationStateCache.current.designChallenge ||
          location.state?.designChallenge);

      console.error("🔥🔥🔥 WORKFLOW INIT DECISION POINT:", {
        workflowId: workflow.id,
        isNewStrategyCreation,
        hasInitiateFlag: !!(
          navigationStateCache.current.initiateStrategyCreation ||
          location.state?.initiateStrategyCreation
        ),
        hasDesignChallenge: !!(
          navigationStateCache.current.designChallenge ||
          location.state?.designChallenge
        ),
        hasStrategyBeenCreated,
        hasExistingPlanContext,
        BREAKDOWN: {
          "cached.initiateStrategyCreation":
            navigationStateCache.current.initiateStrategyCreation,
          "location.state?.initiateStrategyCreation":
            location.state?.initiateStrategyCreation,
          "cached.designChallenge":
            navigationStateCache.current.designChallenge?.substring(0, 50),
          "location.state?.designChallenge":
            location.state?.designChallenge?.substring(0, 50),
          hasStrategyBeenCreated: hasStrategyBeenCreated,
          RESULT: isNewStrategyCreation,
        },
      });

      // Read autonomous mode from workflow config instead of hardcoding
      const isAutonomousWorkflow =
        workflow.uiComponents?.chatPanel?.autonomousMode === true;

      if (isNewStrategyCreation) {
        // NEW STRATEGY CREATION MODE
        console.error("✅ ✅ ✅ SETTING UP NEW STRATEGY CREATION:", {
          workflowId: workflow.id,
          isAutonomousWorkflow,
          willSetStrategyMode: isAutonomousWorkflow,
        });

        // CRITICAL FIX: Explicitly set isStrategyCreationMode based on workflow autonomousMode
        // This ensures Decision Call and Full Strategy both trigger workflow execution through StrategyWorkflowManager
        if (isAutonomousWorkflow) {
          console.log(
            "🚀 SETTING isStrategyCreationMode = 'autonomous' for:",
            workflow.id,
          );
          setIsStrategyCreationMode("autonomous");

          // CRITICAL FIX: Mark workflow as executed in global guard (survives remounts)
          globalWorkflowExecutionGuard.markExecuted(guardKey, productId);

          // CRITICAL FIX: CLEAR the trigger flags from location.state to prevent re-triggering
          // This consumes the "initiateStrategyCreation" flag so subsequent renders don't see it
          if (location.state) {
            window.history.replaceState(
              {
                ...location.state,
                initiateStrategyCreation: false, // Clear the trigger flag
                designChallenge: undefined, // Clear to force using cache
              },
              "",
            );
            console.log(
              "🧹 Cleared trigger flags from location.state to prevent loop",
            );
          }

          // Also clear the navigation cache after workflow starts
          setTimeout(() => {
            navigationStateCache.current.initiateStrategyCreation = false;
            console.log("🧹 Cleared navigation cache trigger flag");
          }, 100);

          // DEFENSIVE: Verify state will update
          setTimeout(() => {
            console.log(
              "⏰ Checking if isStrategyCreationMode was set (should be 'autonomous')",
            );
          }, 100);
        } else {
          console.warn(
            "⚠️ Workflow is NOT autonomous, won't set strategy creation mode",
          );
        }

        // Use cached state if location.state was cleared by Strict Mode
        const designChallenge =
          location.state?.designChallenge ||
          navigationStateCache.current.designChallenge;
        setCurrentDesignChallenge(designChallenge);
        setIsCreatingStrategy(false);
        setIsNewPlan(true);
        setHasStrategyBeenCreated(false);
        setPlanLoadInitialized(false);
        if (workflow.uiComponents?.defaultView && !viewFromUrlRef.current) {
          setCurrentView(workflow.uiComponents.defaultView);
        }
      } else if (hasExistingPlanContext) {
        // EXISTING PLAN VIEW MODE - SAVED PLANS
        console.error(
          "📖 📖 📖 EXISTING PLAN MODE - Setting isStrategyCreationMode = false for:",
          workflow.id,
        );
        setIsStrategyCreationMode(false);
        setIsCreatingStrategy(false);
        setHasStrategyBeenCreated(true);
        setIsNewPlan(false);
        if (workflow.uiComponents?.defaultView && !viewFromUrlRef.current) {
          setCurrentView(workflow.uiComponents.defaultView);
        }
      } else {
        console.error(
          "⚠️ ⚠️ ⚠️ UNKNOWN CONTEXT - Setting isStrategyCreationMode = false",
        );
        setIsStrategyCreationMode(false);
      }
    };

    initializeWorkflow();
  }, [
    location.key,
    location.state, // CRITICAL FIX: React to Decision Call state updates (allows shallow navigation to trigger workflow)
    cachedLaunchState,
    planStateWorkflowId,
    currentPlanId,
    workflowExecutionGuard,
  ]);

  // CRITICAL FIX: Sync currentWorkflowId with planStateWorkflowId
  // This ensures saved plans load their workflow config after database load
  React.useEffect(() => {
    if (!planStateWorkflowId) {
      console.log("🔄 Workflow sync: No planStateWorkflowId yet");
      return;
    }

    // Only sync if currentWorkflowId is different
    if (currentWorkflowId !== planStateWorkflowId) {
      console.log("🔄 Syncing currentWorkflowId from planStateWorkflowId:", {
        from: currentWorkflowId,
        to: planStateWorkflowId,
      });

      const workflow = workflowRegistry.getWorkflow(planStateWorkflowId);
      if (workflow) {
        setCurrentWorkflowId(workflow.id);
        // FIX #32: Preserve enabledFeatures from database when syncing workflow
        // Only preserve if we have MORE features than defaults (meaning database features were loaded)
        setUIConfig((prev) => {
          const newConfig = { ...workflow.uiComponents };
          const defaultFeaturesCount =
            workflow.uiComponents?.enabledFeatures?.length || 0;
          // If we have more features than defaults, keep them (they came from database)
          if (prev?.enabledFeatures?.length > defaultFeaturesCount) {
            newConfig.enabledFeatures = prev.enabledFeatures;
            console.log(
              "🔄 Preserving database enabledFeatures during workflow sync:",
              prev.enabledFeatures,
            );
          }
          // FIX #36: ALWAYS merge views during workflow sync
          if (prev?.views?.length > 0) {
            const defaultViews = workflow.uiComponents?.views || [];
            const prevViews = prev.views || [];
            const mergedViews = [...new Set([...defaultViews, ...prevViews])];
            newConfig.views = mergedViews;
            console.log("🔄 FIX #36: Merging views during workflow sync:", {
              defaultViews,
              prevViews,
              mergedViews,
            });
          }
          return newConfig;
        });
        console.log("✅ Workflow context synced for saved plan");
      } else {
        console.error("❌ No workflow found for ID:", planStateWorkflowId);
      }
    }
  }, [planStateWorkflowId, currentWorkflowId]);

  // FIX #32: Sync enabled_features from database independently of main plan load
  // This ensures navigation tabs always reflect database-saved features even when plan data is cached
  // FIX #41: Added guard to prevent repeated GET requests for the same plan
  React.useEffect(() => {
    const syncEnabledFeaturesFromDatabase = async () => {
      // Only sync if we have a saved plan (not creating new)
      if (!currentPlanId || location.state?.initiateStrategyCreation) {
        return;
      }

      // FIX #41: Skip if we've already synced for this plan ID
      if (syncedPlanIdRef.current === currentPlanId) {
        console.log(
          "📊 FIX #41: Skipping sync - already synced for plan:",
          currentPlanId,
        );
        return;
      }

      try {
        const response = await fetchWithAuth(`/api/plans/${currentPlanId}`);
        if (!response.ok) return;

        const planData = await response.json();
        if (planData.enabled_features) {
          const loadedFeatures = JSON.parse(planData.enabled_features);
          if (Array.isArray(loadedFeatures) && loadedFeatures.length > 0) {
            console.log(
              "📊 FIX #32: Syncing enabled_features from database:",
              loadedFeatures,
            );

            // Mark this plan as synced
            syncedPlanIdRef.current = currentPlanId;

            // Update enabledFeatures state
            setEnabledFeatures(loadedFeatures);

            // FIX #36: Map feature IDs to view IDs for uiConfig.views
            const featureToViewMap = {
              step_by_step_view: "stepByStep",
              calendar_view: "calendar",
              readme_view: "readme",
              slick_view: "slick",
              sow_view: "sow",
              strategy_view: "strategy",
              competitor_analysis_view: "competitor",
            };
            const viewKeys = loadedFeatures
              .map((featureId) => featureToViewMap[featureId] || featureId)
              .filter(Boolean);

            // Update uiConfig.enabledFeatures AND views for navigation tabs
            setUIConfig((prev) => {
              if (!prev) return prev;
              const currentViews = Array.isArray(prev.views) ? prev.views : [];
              const mergedViews = [...new Set([...currentViews, ...viewKeys])];
              // Only update if database has more features than current
              if (
                loadedFeatures.length > (prev.enabledFeatures?.length || 0) ||
                viewKeys.length > currentViews.length
              ) {
                console.log(
                  "📊 FIX #32 + #36: Updating uiConfig from database:",
                  { enabledFeatures: loadedFeatures, views: mergedViews },
                );
                return {
                  ...prev,
                  enabledFeatures: loadedFeatures,
                  views: mergedViews,
                };
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.warn("Failed to sync enabled_features from database:", error);
      }
    };

    // Run with a small delay to ensure workflow init has completed
    const timeoutId = setTimeout(syncEnabledFeaturesFromDatabase, 500);
    return () => clearTimeout(timeoutId);
  }, [currentPlanId, location.state?.initiateStrategyCreation]);

  // CRITICAL FIX: Initialize chat when saved plan finishes loading
  React.useEffect(() => {
    const initializeChatForSavedPlan = async () => {
      // Only initialize if:
      // 1. We have a currentPlanId (plan is loaded)
      // 2. Plan load is initialized (data is ready)
      // 3. Chat is NOT yet initialized
      // 4. This is NOT a new plan creation
      if (
        currentPlanId &&
        planLoadInitialized &&
        !isChatInitialized &&
        !location.state?.initiateStrategyCreation
      ) {
        console.log("🔄 Initializing chat for saved plan:", currentPlanId);
        try {
          await chatStateManager.initializeForPlan(currentPlanId);
          setIsChatInitialized(true); // auto-load handled by dedicated useEffect
          console.log("✅ Chat initialized for saved plan:", currentPlanId);
        } catch (error) {
          console.error("❌ Failed to initialize chat for saved plan:", error);
        }
      }
    };

    initializeChatForSavedPlan();
  }, [
    currentPlanId,
    planLoadInitialized,
    isChatInitialized,
    location.state?.initiateStrategyCreation,
  ]);

  // Auto-load latest conversation once chat is initialized for a saved plan.
  // Runs AFTER isChatInitialized becomes true, guaranteeing FloatingChatbot is
  // mounted and its chatStateManager subscription is active before loadConversation fires.
  useEffect(() => {
    if (!isChatInitialized || !currentPlanId) return;
    if (shouldAutoLaunchWorkflow) return;
    if (location.state?.conversationId || location.state?.loadConversation) return;

    const planConversations = Array.from(chatStateManager.conversations.values())
      .filter(c => c.planId == currentPlanId && c.isPersisted);

    if (planConversations.length > 0) {
      const latest = planConversations
        .sort((a, b) => new Date(b.lastActive || b.createdAt) - new Date(a.lastActive || a.createdAt))[0];
      console.log("🔄 Auto-loading latest conversation for plan", currentPlanId, "->", latest.id);
      chatStateManager.loadConversation(latest.id);
    }
  }, [isChatInitialized, currentPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conversation loading handler
  useEffect(() => {
    const urlHasPlanId =
      window.location.pathname.includes("/plan/") &&
      window.location.pathname.split("/plan/")[1];

    // Handle conversation loading if specified in location.state
    if (location.state?.loadConversation && location.state?.conversationId) {
      console.log(
        "🔥 PlanPage: Loading conversation from location.state:",
        location.state.conversationId,
      );

      const loadConversationFromState = async () => {
        try {
          // Initialize chat state manager for this plan
          await chatStateManager.initializeForPlan(
            currentPlanId || urlHasPlanId,
          );

          // Load the specific conversation
          const conversation = await chatStateManager.loadConversation(
            location.state.conversationId,
          );

          if (conversation) {
            console.log(
              "✅ Successfully loaded conversation",
              location.state.conversationId,
            );
          } else {
            console.error(
              "❌ Failed to load conversation",
              location.state.conversationId,
            );
          }
        } catch (error) {
          console.error(
            "❌ Error loading conversation from location.state:",
            error,
          );
        }
      };

      loadConversationFromState();
    }

    // Clear location state only once to prevent re-triggering
    if (location.state?.initiateStrategyCreation && !window.stateCleared) {
      window.stateCleared = true;
      setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 500);
    }
  }, [
    location.state?.loadConversation,
    location.state?.conversationId,
    location.state?.initiateStrategyCreation,
    currentPlanId,
  ]);

  // Loading phrase rotation effect - start immediately when loading screen appears
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex(
        (prevIndex) => (prevIndex + 1) % loadingPhrases.length,
      );
    }, 2000);

    return () => clearInterval(interval);
  }, [loadingPhrases]);

  // Update strategy creation phrase
  useEffect(() => {
    setStrategyCreationPhrase(loadingPhrases[currentPhraseIndex]);
  }, [currentPhraseIndex, loadingPhrases]);

  const renderCurrentView = () => {
    if (!currentView) {
      console.log("No current view set, defaulting to strategy");
      return renderStrategyView();
    }

    const commonProps = {
      // Every view sizes itself against the content area, not the window, because
      // the narrative panel changes how much room the view actually has.
      contentPanelWidth,
      currentDesignChallenge,
      selectedPrograms,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      finalProgramSequence,
      totalDuration,
      handleComponentAction,
      programsOfWork,
      methods,
      tools,
      deliverables,
      currentPlanId,
      currentWorkflowId,
      uiConfig,
    };

    switch (currentView) {
      case "pulse":
      case "pulse_view":
        return renderPulseView(commonProps);
      case "strategy":
        return renderStrategyView(commonProps);
      case "stepByStep":
      case "step_by_step_view":
        return renderStepByStepView(commonProps);
      case "calendar":
        return renderCalendarView(commonProps);
      case "slick":
        return renderSlickView(commonProps);
      case "sow":
        return renderNotionSOWView(commonProps);
      case "readme":
        return renderReadmeView(commonProps);
      case "competitor":
      case "competitor_analysis_view":
        return renderCompetitorAnalysisView(commonProps);
      case "productStrategy":
      case "product_strategy_view":
        return renderProductStrategyView(commonProps);
      case "decisions":
      case "decisions_view":
        return renderDecisionsView(commonProps);
      case "designAuthority":
      case "design_authority":
        return renderDesignAuthorityView(commonProps);
      case "newView":
        return renderAddViewSelector();
      default:
        console.warn("Unknown view:", currentView, "defaulting to strategy");
        return renderStrategyView(commonProps);
    }
  };

  const renderStrategyView = (props = {}) => {
    const viewProps = {
      shortSummary: shortSummary || "",
      setShortSummary,
      goalStatement: goalStatement || "",
      setGoalStatement,
      problemStatement: problemStatement || "",
      setProblemStatement,
      targetStatement: targetStatement || "",
      setTargetStatement,
      objectivesStatement: objectivesStatement || [],
      setObjectivesStatement,
      setIsSaved,
      recentlyApplied,
      fieldUpdates,
      clearRecentlyApplied: () => setRecentlyApplied(null),
      onSendChatPrompt: (message, refinementContext = null) => {
        if (chatbotRef.current?.handleSend) {
          if (isNarrativeCollapsed) {
            setIsNarrativeCollapsed(false);
          }
          setTimeout(() => {
            chatbotRef.current.handleSend(message, refinementContext);
          }, 100);
        }
      },
      contentPanelWidth,
      ...props,
    };

    return (
      <DesignStrategyVision
        {...viewProps}
      />
    );
  };

  const renderStepByStepView = (props = {}) => {
    const viewProps = {
      finalProgramSequence: finalProgramSequence || [],
      setFinalProgramSequence,
      setSelectedPrograms,
      setTotalDuration,
      setIsSaved,
      handleRemoveProgram: handleDeleteProgram,
      formatDuration,
      handleAddToolClick,
      handleActivityCardClick,
      handleDurationChange,
      handleRemoveTool,
      handleProgramFieldChange,
      handleGenerateField,
      loadingState: loadingState || {},
      handleAddProgramClick,
      totalDuration: totalDuration || 0,
      currentDesignChallenge,
      selectedPrograms,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      handleComponentAction,
      programsOfWork,
      methods,
      tools,
      deliverables,
      currentPlanId,
      currentWorkflowId,
      uiConfig,
      chatbotRef,
      isNarrativeCollapsed,
      onChatCollapseChange,
      // Add direct function to expand embedded chat panel
      expandEmbeddedChatPanel: () => {
        console.log("🔧 DIRECT: expandEmbeddedChatPanel called");
        setIsNarrativeCollapsed(false);
        if (onChatCollapseChange) onChatCollapseChange(false);
      },
      // Agent-driven workflow trigger props
      pendingFeatureWorkflowTrigger,
      setPendingFeatureWorkflowTrigger,
      // Navigation context from strategy next-step CTAs
      navigationContext,
      onClearNavigationContext: () => setNavigationContext(null),
      // Chat delegation — sends a message to the agent to trigger generate_action_plan tool
      onSendChatPrompt: (message, refinementContext = null) => {
        if (chatbotRef.current?.handleSend) {
          if (isNarrativeCollapsed) {
            setIsNarrativeCollapsed(false);
          }
          setTimeout(() => {
            chatbotRef.current.handleSend(message, refinementContext);
          }, 100);
        }
      },
      contentPanelWidth,
      ...props,
    };

    console.log("Rendering StepByStepView with props:", Object.keys(viewProps));
    return <StepByStepView {...viewProps} />;
  };

  const renderCalendarView = (props = {}) => {
    const viewProps = {
      programs: finalProgramSequence || [],
      onStartOptionChange: (programName, option) => {
        setFinalProgramSequence((prevSequence) =>
          prevSequence.map((program) =>
            program.program_of_work === programName
              ? { ...program, start_option: option }
              : program,
          ),
        );
        setIsSaved(false);
      },
      setIsSaved,
      currentDesignChallenge,
      selectedPrograms,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      finalProgramSequence,
      totalDuration,
      handleComponentAction,
      programsOfWork,
      methods,
      tools,
      deliverables,
      currentPlanId,
      currentWorkflowId,
      uiConfig,
      contentPanelWidth,
      ...props,
    };

    console.log("Rendering CalendarView with props:", Object.keys(viewProps));
    return <CalendarView {...viewProps} />;
  };

  const renderSlickView = (props = {}) => {
    const viewProps = {
      programs: finalProgramSequence || [],
      currentDesignChallenge,
      selectedPrograms,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      finalProgramSequence,
      totalDuration,
      handleComponentAction,
      programsOfWork,
      methods,
      tools,
      deliverables,
      currentPlanId,
      currentWorkflowId,
      uiConfig,
      contentPanelWidth,
      ...props,
    };

    console.log("Rendering SlickView with props:", Object.keys(viewProps));
    return <SlickView {...viewProps} />;
  };

  const renderNotionSOWView = (props = {}) => {
    const viewProps = {
      finalProgramSequence: finalProgramSequence || [],
      currentDesignChallenge,
      shortSummary,
      setShortSummary,
      goalStatement,
      setGoalStatement,
      problemStatement,
      setProblemStatement,
      targetStatement,
      setTargetStatement,
      objectivesStatement,
      setObjectivesStatement,
      deliverablesStatement,
      setDeliverablesStatement,
      metricsStatement,
      setMetricsStatement,
      stakeholdersCustomersInvolvement,
      setStakeholdersCustomersInvolvement,
      totalDuration,
      setIsSaved,
      formatDuration,
      ...props,
    };

    console.log("Rendering NotionStyleSOW with props:", Object.keys(viewProps));
    return <NotionStyleSOW {...viewProps} />;
  };

  const renderReadmeView = (props = {}) => {
    const viewProps = {
      currentDesignChallenge,
      finalProgramSequence: finalProgramSequence || [],
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      totalDuration,
      currentPlanId,
      activeDocumentGenerationId,
      contentPanelWidth,
      onSendChatPrompt: (message, refinementContext = null) => {
        if (!chatbotRef.current?.handleSend) return;
        if (isNarrativeCollapsed) setIsNarrativeCollapsed(false);
        setTimeout(() => chatbotRef.current.handleSend(message, refinementContext), 100);
      },
      ...props,
    };

    console.log(
      "Rendering ReadmeGenerator with props:",
      Object.keys(viewProps),
    );
    return <ReadmeGenerator {...viewProps} />;
  };

  const renderCompetitorAnalysisView = (props = {}) => {
    const viewProps = {
      currentPlanId,
      currentDesignChallenge,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      navigationContext,
      onClearNavigationContext: () => setNavigationContext(null),
      onSendChatPrompt: (message, refinementContext = null) => {
        if (chatbotRef.current?.handleSend) {
          if (isNarrativeCollapsed) {
            setIsNarrativeCollapsed(false);
          }
          setTimeout(() => {
            chatbotRef.current.handleSend(message, refinementContext);
          }, 100);
        }
      },
      contentPanelWidth,
      ...props,
    };

    console.log(
      "Rendering CompetitorAnalysisView with props:",
      Object.keys(viewProps),
    );
    return <CompetitorAnalysisView {...viewProps} />;
  };

  const renderProductStrategyView = (props = {}) => {
    const viewProps = {
      currentPlanId,
      currentDesignChallenge,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      stakeholdersCustomersInvolvement,
      setStakeholdersCustomersInvolvement,
      finalProgramSequence: finalProgramSequence || [],
      totalDuration: totalDuration || 0,
      formatDuration,
      loadingState: loadingState || {},
      handleGenerateFullPlanField,
      setIsSaved,
      uiConfig,
      recentlyApplied,
      fieldUpdates,
      clearRecentlyApplied: () => setRecentlyApplied(null),
      onNavigateToView: (viewKey, context = null) => {
        if (context) {
          setNavigationContext(context);
        }
        setCurrentView(viewKey);
      },
      onEnableFeatureAndNavigate: async (featureId, viewKey, context = null) => {
        try {
          await handleComponentAction({
            type: "component_capability",
            data: {
              capability: "enable_feature",
              data: { featureId },
            },
          });
          if (context) {
            setNavigationContext(context);
          }
          startTransition(() => {
            setCurrentView(viewKey);
          });
        } catch (error) {
          console.error("Failed to enable feature and navigate:", error);
        }
      },
      onSendChatPrompt: (message, refinementContext = null) => {
        if (chatbotRef.current?.handleSend) {
          if (isNarrativeCollapsed) {
            setIsNarrativeCollapsed(false);
          }
          setTimeout(() => {
            chatbotRef.current.handleSend(message, refinementContext);
          }, 100);
        }
      },
      onUpdateStrategyField: async (fieldName, data) => {
        const res = await fetchWithAuth(`/api/product-strategy/plan/${currentPlanId}/update`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Update failed');
        window.dispatchEvent(new CustomEvent('productStrategyUpdated', {
          detail: { updatedFields: [fieldName], planId: currentPlanId },
        }));
        return result;
      },
      // Narrow layout: tablet with chat open (content area < 700px)
      isNarrowLayout: isMobile !== false
        ? false  // mobile: chat is full-screen overlay, content gets full width
        : !isNarrativeCollapsed && (windowWidth - leftPanelWidth) < 700,
      contentPanelWidth,
      ...props,
    };

    console.log(
      "Rendering ProductStrategyView with props:",
      Object.keys(viewProps),
    );
    return (
      <ProductStrategyView
        {...viewProps}
      />
    );
  };

  const renderDesignAuthorityView = (props = {}) => {
    return <DesignAuthorityView currentPlanId={currentPlanId} {...props} />;
  };

  const renderPulseView = (props = {}) => {
    return (
      <PulseView
        planId={currentPlanId}
        onSendChatPrompt={(message) => {
          if (chatbotRef.current?.handleSend) {
            if (isNarrativeCollapsed) setIsNarrativeCollapsed(false);
            setTimeout(() => { chatbotRef.current.handleSend(message); }, 100);
          }
        }}
        onNavigateToView={(viewKey, context = null) => {
          if (viewKey === "mcp")       { onMcpModalOpen();          return; }
          if (viewKey === "connectors") { navigate("/connectors");   return; }
          if (viewKey === "share")     { onShareModalOpen();        return; }
          if (viewKey === "learnings") { onLearningsSidebarOpen();  return; }
          if (context) setNavigationContext(context);
          setCurrentView(viewKey);
        }}
        strategyLifecycleStatus={strategyLifecycleStatus}
        onStrategyLifecycleStatusChange={handleStrategyLifecycleStatusChange}
        {...props}
      />
    );
  };

  const renderDecisionsView = (props = {}) => {
    const viewProps = {
      currentPlanId,
      recentlyApplied,
      strategyResetKey,
      onLearningApplied: (payload) => { setRecentlyApplied(payload); fetchFieldUpdates(); refreshPlanContent(); },
      fieldUpdates,
      onOpenLearnings: onLearningsSidebarOpen,
      pendingLearningsCount: learningsPendingCount,
      navigationContext,
      onClearNavigationContext: () => setNavigationContext(null),
      onNavigateToView: (viewKey) => {
        if (viewKey === "mcp")      { onMcpModalOpen();          return; }
        if (viewKey === "learnings") { onLearningsSidebarOpen();  return; }
        setCurrentView(viewKey);
      },
      onSendChatPrompt: (message) => {
        if (chatbotRef.current?.handleSend) {
          if (isNarrativeCollapsed) {
            setIsNarrativeCollapsed(false);
          }
          setTimeout(() => {
            chatbotRef.current.handleSend(message);
          }, 100);
        }
      },
      // Conversational card actions draft the message for the person to send,
      // so opening a discussion never spends credits on its own.
      onPrefillChatPrompt: (message) => {
        if (isNarrativeCollapsed) {
          setIsNarrativeCollapsed(false);
        }
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("pabs:prefillPlanChat", {
            detail: { planId, source: "decision_card", message },
          }));
        }, 100);
      },
      isNarrowLayout: isMobile !== false
        ? false
        : !isNarrativeCollapsed && (windowWidth - leftPanelWidth) < 700,
      contentPanelWidth,
      ...props,
    };

    return <DecisionsView {...viewProps} />;
  };

  // Handler for when a feature is selected in AddViewSelector
  const handleAddViewFeatureSelect = async (featureId) => {
    console.log("🎯 AddViewSelector: Feature selected:", featureId);

    // Check if enabling step_by_step_view with no program data
    const hasProgramData =
      finalProgramSequence && finalProgramSequence.length > 0;
    const needsAIWorkflow =
      featureId === "step_by_step_view" && !hasProgramData;

    console.log("🎯 AddViewSelector: Validation check:", {
      featureId,
      hasProgramData,
      programCount: finalProgramSequence?.length || 0,
      needsAIWorkflow,
    });

    try {
      // Call the feature enablement capability through ComponentActionHandler
      const result = await handleComponentAction({
        type: "component_capability",
        data: {
          capability: "enable_feature",
          data: { featureId },
        },
      });

      console.log("🎯 AddViewSelector: Feature enablement result:", result);

      // Map featureId to viewKey for navigation
      const featureToViewMap = {
        strategy_view: "strategy",
        product_strategy_view: "productStrategy",
        step_by_step_view: "stepByStep",
        calendar_view: "calendar",
        slick_view: "slick",
        sow_view: "sow",
        readme_view: "readme",
              competitor_analysis_view: "competitor",
              design_authority: "designAuthority",
      };

      const viewKey = featureToViewMap[featureId] || featureId;

      // Close the add view selector and switch to the newly enabled view
      setShowAddView(false);
      startTransition(() => {
        setCurrentView(viewKey);
      });

      // If step_by_step_view was enabled without program data, trigger AI workflow
      if (needsAIWorkflow) {
        console.log(
          "🎯 AddViewSelector: No program data detected - triggering MANUAL AI workflow with progress UI",
        );

        // IMPORTANT: Open the chat panel first so user can see the workflow progress
        setIsNarrativeCollapsed(false);
        console.log(
          "🎯 AddViewSelector: Opened chat panel for workflow visibility",
        );

        // Give a brief delay to let the view switch and render
        setTimeout(() => {
          // Use the new triggerManualFeatureWorkflow method for autonomous workflow with progress UI
          // This is different from handleSend (conversational) - this triggers the full workflow
          if (chatbotRef.current?.triggerManualFeatureWorkflow) {
            console.log(
              "🎯 AddViewSelector: Using triggerManualFeatureWorkflow for autonomous workflow",
            );
            chatbotRef.current.triggerManualFeatureWorkflow(featureId);
          } else {
            // Fallback to conversational approach if method not available
            console.warn(
              "🎯 AddViewSelector: triggerManualFeatureWorkflow not available, falling back to chat",
            );
            if (chatbotRef.current?.handleSend) {
              const triggerMessage =
                "Please create a comprehensive action plan with programs of work, stages, and tools based on my design challenge.";
              chatbotRef.current.handleSend(triggerMessage);
            }
          }
        }, 500);
      }
    } catch (error) {
      console.error("🎯 AddViewSelector: Error enabling feature:", error);
    }
  };

  // Handler to close the add view selector
  const handleCloseAddView = () => {
    setShowAddView(false);
    // Return to the previous view (default to strategy if none)
    const lastView =
      dynamicNavButtons.find((btn) => uiConfig?.views?.includes(btn.viewKey))
        ?.config?.id || "strategy";
    startTransition(() => {
      setCurrentView(lastView);
    });
  };

  // Render AddViewSelector component
  const renderAddViewSelector = () => {
    return (
      <AddViewSelector
        enabledFeatures={enabledFeatures || []}
        onFeatureSelect={handleAddViewFeatureSelect}
        onClose={handleCloseAddView}
      />
    );
  };

  // ENHANCED data sanitization to prevent corruption while preserving program data
  const sanitizeProgramsForSave = useCallback((programs) => {
    console.log("sanitizeProgramsForSave called with:", {
      isArray: Array.isArray(programs),
      length: programs?.length,
      type: typeof programs,
      firstItem: programs?.[0],
      programNames: Array.isArray(programs)
        ? programs.map((p) => p?.program_of_work || p?.name).slice(0, 3)
        : "Not array",
    });

    if (!Array.isArray(programs)) {
      console.warn("Programs is not an array, returning empty array");
      return [];
    }

    // Return empty array if no programs (this is expected for new workflows)
    if (programs.length === 0) {
      console.log("No programs to sanitize - returning empty array");
      return [];
    }

    // CRITICAL: Detect massive data corruption early
    if (programs.length > 100) {
      console.error(
        "MASSIVE CORRUPTION DETECTED: " +
          programs.length +
          " programs - rejecting to prevent crash",
      );
      return [];
    }

    console.log("Frontend sanitizing " + programs.length + " programs");

    // Deduplicate programs by name first
    const seenNames = new Set();
    const deduplicatedPrograms = programs.filter((program) => {
      const name = program?.program_of_work || program?.name || program?.title;
      if (!name || seenNames.has(name)) {
        console.log("Removing duplicate program: " + name);
        return false;
      }
      seenNames.add(name);
      return true;
    });

    console.log(
      "After deduplication: " + deduplicatedPrograms.length + " programs",
    );

    // Hard limit to 10 programs maximum
    const limitedPrograms = deduplicatedPrograms.slice(0, 10);

    const sanitizedPrograms = limitedPrograms
      .map((program, index) => {
        if (!program || typeof program !== "object") {
          console.warn(
            "Invalid program object at index " + index + ", skipping",
          );
          return null;
        }

        // Enhanced structure preservation with validation - PRESERVE TOOLS DATA
        const cleanProgram = {
          program_of_work: String(
            program.program_of_work ||
              program.name ||
              program.title ||
              "Unnamed Program",
          ).substring(0, 100),
          phase: String(program.phase || "Discovery").substring(0, 30),
          goal: String(program.goal || program.outcome || "").substring(0, 500),
          description: String(program.description || "").substring(0, 500),
          totalProgramLength: Math.min(
            Number(program.totalProgramLength) ||
              Number(program.duration) ||
              40,
            1000,
          ), // Max 1000 hours, default to 40
          isIncluded: program.hasOwnProperty("isIncluded")
            ? program.isIncluded
            : true, // Preserve exact isIncluded state from user input without Boolean conversion

          // Preserve essential arrays with validation
          methods: Array.isArray(program.methods)
            ? program.methods
                .slice(0, 5)
                .map((m) => String(m).substring(0, 100))
            : Array.isArray(program.related_methods)
              ? program.related_methods
                  .slice(0, 5)
                  .map((m) => String(m).substring(0, 100))
              : [],

          // Preserve Stages array - CRITICAL for component rendering
          Stages: Array.isArray(program.Stages)
            ? program.Stages.slice(0, 5).map((s) => String(s).substring(0, 100))
            : Array.isArray(program.stages)
              ? program.stages
                  .slice(0, 5)
                  .map((s) => String(s).substring(0, 100))
              : [],

          // Preserve focus areas if available
          focus: Array.isArray(program.Focus)
            ? program.Focus.slice(0, 3).map((f) => String(f).substring(0, 100))
            : Array.isArray(program.focus)
              ? program.focus
                  .slice(0, 3)
                  .map((f) => String(f).substring(0, 100))
              : [],

          // Preserve deliverables if available as simple array
          expectedDeliverables: Array.isArray(program.expectedDeliverables)
            ? program.expectedDeliverables
                .slice(0, 5)
                .map((d) => String(d).substring(0, 200))
            : [],

          // CRITICAL: Preserve stakeholdersInvolvement field as string (like goals/deliverables)
          stakeholdersInvolvement:
            typeof program.stakeholdersInvolvement === "string"
              ? program.stakeholdersInvolvement.substring(0, 500)
              : Array.isArray(program.stakeholdersInvolvement)
                ? program.stakeholdersInvolvement.join("\n").substring(0, 500)
                : "",

          // PRESERVE FULL toolsByStage structure with smart optimization
          toolsByStage:
            program.toolsByStage && typeof program.toolsByStage === "object"
              ? Object.keys(program.toolsByStage).reduce(
                  (optimizedStages, stageName) => {
                    const stage = program.toolsByStage[stageName];
                    if (stage && typeof stage === "object") {
                      optimizedStages[String(stageName)] = {
                        tools: Array.isArray(stage.tools)
                          ? stage.tools.slice(0, 10).map((tool, toolIndex) => ({
                              id:
                                (tool && tool.id) ||
                                "tool-" +
                                  Date.now() +
                                  "-" +
                                  toolIndex +
                                  "-" +
                                  Math.random(),
                              tool:
                                (tool && (tool.tool || tool.name)) ||
                                "Unknown Tool",
                              description: (tool && tool.description) || "",
                              duration: Number(tool && tool.duration) || 1,
                              related_stage:
                                (tool && tool.related_stage) || stageName,
                              complexity: (tool && tool.complexity) || "simple",
                              related_methods:
                                tool && Array.isArray(tool.related_methods)
                                  ? tool.related_methods
                                  : tool &&
                                      typeof tool.related_methods === "string"
                                    ? tool.related_methods
                                        .split(",")
                                        .map((m) => m.trim())
                                    : [],
                            }))
                          : [],
                        totalDuration: Number(stage.totalDuration) || 0,
                      };
                    }
                    return optimizedStages;
                  },
                  {},
                )
              : {},

          // Store basic metadata
          score: Number(program.score) || 0,
          similarity_score: Number(program.similarity_score) || 0,
        };

        // Final size check and truncation - but preserve essential tool data
        const jsonSize = JSON.stringify(cleanProgram).length;
        if (jsonSize > 8000) {
          // Increased limit to accommodate tool data
          console.warn(
            "Program " +
              index +
              " too large (" +
              jsonSize +
              " chars), truncating descriptions only",
          );
          cleanProgram.description = cleanProgram.description.substring(0, 100);
          cleanProgram.goal = cleanProgram.goal.substring(0, 200);

          // If still too large, limit tools per stage but keep structure
          if (JSON.stringify(cleanProgram).length > 8000) {
            Object.keys(cleanProgram.toolsByStage).forEach((stageName) => {
              if (cleanProgram.toolsByStage[stageName].tools) {
                cleanProgram.toolsByStage[stageName].tools =
                  cleanProgram.toolsByStage[stageName].tools.slice(0, 5);
              }
            });
          }
        }

        console.log("Sanitized program " + index + ":", {
          name: cleanProgram.program_of_work,
          phase: cleanProgram.phase,
          duration: cleanProgram.totalProgramLength,
          methodsCount: cleanProgram.methods.length,
          stagesCount: cleanProgram.Stages.length,
          toolStagesCount: Object.keys(cleanProgram.toolsByStage).length,
          size: JSON.stringify(cleanProgram).length,
        });

        return cleanProgram;
      })
      .filter(Boolean); // Remove any null programs

    console.log("Sanitization complete:", {
      inputCount: programs.length,
      outputCount: sanitizedPrograms.length,
      totalSize: JSON.stringify(sanitizedPrograms).length,
    });

    return sanitizedPrograms;
  }, []);

  // Extract complex expressions for dependency array
  const selectedProgramsIncludedState = selectedPrograms
    ?.map((p) => `${p.program_of_work}:${p.isIncluded}`)
    .join(",");
  const finalProgramSequenceIncludedState = finalProgramSequence
    ?.map((p) => `${p.program_of_work}:${p.isIncluded}`)
    .join(",");

  // Drag handlers for resizable divider - MOVED BEFORE EARLY RETURNS
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const newWidth = e.clientX;
      // Set minimum and maximum widths
      const minWidth = 300;
      const maxWidth = window.innerWidth * 0.7; // Max 70% of screen width

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setLeftPanelWidth(newWidth);
      }
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add event listeners for mouse events - MOVED BEFORERETURNS
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Scroll event listener to detect when tabs become stuck
  useEffect(() => {
    // Use a small delay to ensure refs are attached after render
    const timer = setTimeout(() => {
      const scrollContainer = rightPanelScrollRef.current;
      const sentinel = tabsSentinelRef.current;

      if (!scrollContainer || !sentinel) {
        return;
      }

      const handleScroll = () => {
        const sentinelRect = sentinel.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        // Tabs are stuck when sentinel is above the container's top
        const isStuck = sentinelRect.top < containerRect.top;
        setIsTabsStuck(isStuck);
      };

      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      handleScroll();

      // Store cleanup function
      scrollContainer._stickyCleanup = () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      const scrollContainer = rightPanelScrollRef.current;
      if (scrollContainer && scrollContainer._stickyCleanup) {
        scrollContainer._stickyCleanup();
      }
    };
  }, [hasStrategyBeenCreated, shortSummary]);

  // Floating nav — PABS brand colours (brand.600 = #4a5568 charcoal, brand.700 = #2d3748)
  const nr = 74, ng = 85, nb = 104;   // #4a5568 brand.600
  const navBg = 'rgba(255,255,255,0.92)';
  const navBorder = '1px solid rgba(0,0,0,0.08)';
  const navMutedClr = '#718096';
  const navActiveTextColor = '#ffffff';

  // Floating nav — keep centred within the right content panel as chat opens/closes.
  // No sidebar offset anymore — chat panel is the only left-side dead space.
  const navTransform = useMemo(() => {
    if (isMobile !== false) return 'translateX(-50%)';
    const chatPx = isNarrativeCollapsed ? 0 : leftPanelWidth;
    const offset = chatPx / 2;
    return `translateX(calc(-50% + ${offset}px))`;
  }, [isMobile, isNarrativeCollapsed, leftPanelWidth]);

  const navMaxWidth = useMemo(() => {
    if (isMobile !== false) return 'calc(100vw - 32px)';
    const chatPx = isNarrativeCollapsed ? 0 : leftPanelWidth;
    return `calc(100vw - ${chatPx + 32}px)`;
  }, [isMobile, isNarrativeCollapsed, leftPanelWidth]);

  // Floating nav — split tabs into inline + overflow, keeping active always visible
  const filteredNavButtons = useMemo(() => {
    if (!uiConfig?.views || !dynamicNavButtons.length) return [];
    return dynamicNavButtons.filter(({ viewKey }) => uiConfig.views.includes(viewKey));
  }, [dynamicNavButtons, uiConfig]);

  const { inlineTabs, overflowTabs } = useMemo(() => {
    const tabs = filteredNavButtons;
    if (tabs.length <= effectiveMaxInline) return { inlineTabs: tabs, overflowTabs: [] };
    const slots = effectiveMaxInline - 1; // last slot = "..." button
    let inline = tabs.slice(0, slots);
    let overflow = tabs.slice(slots);
    const activeOverflow = overflow.find(t => t.config.id === currentView);
    if (activeOverflow) {
      const bumped = inline[inline.length - 1];
      inline = [...inline.slice(0, -1), activeOverflow];
      overflow = [...(bumped ? [bumped] : []), ...overflow.filter(t => t.config.id !== currentView)];
    }
    return { inlineTabs: inline, overflowTabs: overflow };
  }, [filteredNavButtons, effectiveMaxInline, currentView]);

  // Floating nav — close overflow dropdown when clicking outside
  useEffect(() => {
    if (!overflowOpen) return;
    const handler = (e) => {
      if (overflowBtnRef.current && !overflowBtnRef.current.contains(e.target))
        setOverflowOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  // Helper function to serialize program tool structures for comparison
  const serializeProgramTools = useCallback((programs) => {
    if (!Array.isArray(programs)) return "";
    return programs
      .map((program) => {
        if (!program.toolsByStage) return "";
        return Object.keys(program.toolsByStage)
          .sort()
          .map((stageName) => {
            const stage = program.toolsByStage[stageName];
            if (!stage || !Array.isArray(stage.tools)) return "";
            return (
              stageName +
              ":" +
              stage.tools
                .map((tool) => tool.id || tool.tool)
                .sort()
                .join(",")
            );
          })
          .join("|");
      })
      .join("||");
  }, []);

  // Helper function to serialize program start options for comparison
  const serializeProgramStartOptions = useCallback((programs) => {
    if (!Array.isArray(programs)) return "";
    return programs
      .map(
        (program) =>
          `${program.program_of_work}:${program.start_option || "after"}`,
      )
      .join("|");
  }, []);

  // Create serialized versions for dependency tracking
  const serializedProgramTools = useMemo(
    () => serializeProgramTools(finalProgramSequence),
    [finalProgramSequence, serializeProgramTools],
  );

  const serializedStartOptions = useMemo(
    () => serializeProgramStartOptions(finalProgramSequence),
    [finalProgramSequence, serializeProgramStartOptions],
  );

  // Watch for changes that should trigger an unsaved state and auto-save
  useEffect(() => {
    console.log("AUTO-SAVE USEEFFECT TRIGGERED:", {
      isStrategyCreationMode,
      isCreatingStrategy,
      planState,
      isInitialLoad: isInitialLoad.current,
      hasShortSummary: !!shortSummary,
      selectedProgramsLength: selectedPrograms?.length || 0,
      finalProgramSequenceLength: finalProgramSequence?.length || 0,
      totalDuration,
      currentPlanId,
      planLoadInitialized,
      isSaved,
    });

    // EMERGENCY BRAKE: Prevent rapid-fire auto-saves
    const autoSaveKey = `autoSave_${currentPlanId}`;
    const lastAutoSave = window[autoSaveKey] || 0;
    const timeSinceLastAutoSave = Date.now() - lastAutoSave;
    if (timeSinceLastAutoSave < 15000) {
      // Increased to 15 seconds minimum between auto-saves
      console.log(
        "AUTO-SAVE BLOCKED - too frequent, last save was",
        timeSinceLastAutoSave,
        "ms ago",
      );
      return;
    }

    // Define hasValidData early to avoid temporal dead zone issues
    const hasValidData =
      shortSummary ||
      selectedPrograms?.length > 0 ||
      finalProgramSequence?.length > 0;

    // NEVER auto-save during strategy creation workflow
    if (isStrategyCreationMode || isCreatingStrategy) {
      console.log("AUTO-SAVE BLOCKED - strategy creation in progress");
      return;
    }

    // CRITICAL: Don't auto-save immediately after strategy completion to prevent data loss
    const timeSinceCompletion =
      Date.now() - (window.strategyCompletionTime || 0);
    if (timeSinceCompletion < 30000 && window.strategyCompletionTime) {
      console.log("AUTO-SAVE BLOCKED - recent strategy completion protection");
      return;
    }

    // Don't auto-save if currently saving or loading
    if (isSaving || planState === "saving" || planState === "loading") {
      console.log("AUTO-SAVE BLOCKED - currently saving or loading");
      return;
    }

    // STRONGER: Block auto-save during initial load OR if plan not initialized
    if (isInitialLoad.current || !planLoadInitialized) {
      console.log("AUTO-SAVE BLOCKED - initial load or plan not initialized");
      return;
    }

    // CRITICAL FIX: Don't auto-save if plan is already saved and no changes detected
    if (isSaved && planState === "saved") {
      console.log(
        "AUTO-SAVE BLOCKED - plan already saved, no changes detected",
      );
      return;
    }

    // STRONGER: Only auto-save if we have valid data AND current plan ID AND plan is marked as unsaved
    if (hasValidData && currentPlanId && !isSaved) {
      console.log("Auto-save conditions met - scheduling save");

      // Set emergency brake timestamp
      window[autoSaveKey] = Date.now();

      // Clear any existing timeout to prevent multiple saves
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Auto-save after a delay
      saveTimeoutRef.current = setTimeout(() => {
        if (!isSaving && planState !== "saving" && !isSaved) {
          console.log("🔄 Executing auto-save");
          savePlan().then(() => {
            chatStateManager.notifyListeners({ type: "conversation_plan_updated" });
          });
        }
      }, 5000); // Increased to 5 seconds for more stability
    }
  }, [
    // Include all data changes that should trigger auto-save
    planName,
    shortSummary,
    problemStatement,
    goalStatement,
    targetStatement,
    deliverablesStatement,
    metricsStatement,
    stakeholdersCustomersInvolvement,
    selectedPrograms?.length,
    finalProgramSequence?.length,
    totalDuration,
    selectedProgramsIncludedState,
    finalProgramSequenceIncludedState,
    serializedProgramTools,
    serializedStartOptions,
    // Include state that affects auto-save logic
    isStrategyCreationMode,
    isCreatingStrategy,
    planState,
    currentPlanId,
    planLoadInitialized,
    isSaving,
    isSaved,
    savePlan,
  ]);

  // FIX #33: Load dynamic navigation buttons from Core Features Registry
  // CRITICAL: Depend on `enabledFeatures` state (not uiConfig.enabledFeatures)
  // because uiConfig reference doesn't change when database features load
  useEffect(() => {
    const loadDynamicNavigation = async () => {
      console.log(
        "🔄 FIX #33: loadDynamicNavigation triggered with enabledFeatures:",
        enabledFeatures,
      );

      if (enabledFeatures?.length > 0) {
        try {
          // Get plan management views from the registry
          const response = await fetchWithAuth(
            "/api/core-features-registry/plan-management-views",
          );
          if (response.ok) {
            const data = await response.json();

            // Filter to only enabled features and create navigation buttons
            const enabledButtons = orderPlanNavigationFeatures(enabledFeatures)
              .map((featureId) => {
                const feature = data.plan_management_views[featureId];
                if (feature?.button_config) {
                  return {
                    viewKey: feature.button_config.id,
                    config: feature.button_config,
                    featureInfo: {
                      name: feature.name,
                      component: feature.component,
                      description: feature.description,
                      capabilities: feature.capabilities,
                    },
                  };
                }
                console.log(
                  `⚠️ FIX #33: No button_config for feature: ${featureId}`,
                );
                return null;
              })
              .filter(Boolean);

            setDynamicNavButtons(enabledButtons);

            // FIX #33: Also update uiConfig.views to include the loaded viewKeys
            // This ensures the filter at render time includes all buttons
            const viewKeys = enabledButtons.map((b) => b.viewKey);
            setUIConfig((prev) => {
              if (!prev) return prev;
              const currentViews = prev.views || [];
              const mergedViews = [...new Set([...currentViews, ...viewKeys])];
              console.log("🎯 FIX #33: Updating uiConfig.views:", {
                currentViews,
                viewKeys,
                mergedViews,
              });
              return { ...prev, views: mergedViews };
            });

            console.log(
              "🎯 Dynamic navigation loaded from Core Features Registry:",
              {
                enabledFeatures: enabledFeatures,
                loadedButtons: enabledButtons.length,
                buttons: enabledButtons.map((b) => b.config.label),
                viewKeys: viewKeys,
              },
            );
          }
        } catch (error) {
          console.error("Failed to load dynamic navigation:", error);
          // Fallback to static configuration
          setDynamicNavButtons([
            { viewKey: "strategy", config: strategyButtonConfig },
            { viewKey: "slick", config: slickButtonConfig },
            { viewKey: "stepByStep", config: stepByStepButtonConfig },
            { viewKey: "calendar", config: calendarButtonConfig },
          ]);
        }
      } else {
        console.log(
          "⚠️ FIX #33: enabledFeatures is empty or undefined:",
          enabledFeatures,
        );
      }
    };

    loadDynamicNavigation();
  }, [enabledFeatures]);

  // Check if all plan management view features are enabled
  useEffect(() => {
    const checkAllFeaturesEnabled = async () => {
      try {
        const enabledFeaturesParam = enabledFeatures?.join(",") || "";
        const response = await fetchWithAuth(
          `/api/core-features-registry/available-features?enabledFeatures=${encodeURIComponent(enabledFeaturesParam)}`,
        );

        if (response.ok) {
          const data = await response.json();
          setAllFeaturesEnabled(data.allFeaturesEnabled);
          console.log(
            "🎯 All features enabled check:",
            data.allFeaturesEnabled,
          );
        }
      } catch (error) {
        console.error("Failed to check if all features enabled:", error);
      }
    };

    if (enabledFeatures?.length > 0) {
      checkAllFeaturesEnabled();
    }
  }, [enabledFeatures]);

  // Set initial saved state based on strategy creation mode
  useEffect(() => {
    if (isStrategyCreationMode || isCreatingStrategy) {
      setIsSaved(false); // Show "Creating..." during workflow
      setPlanState("creating");
    }
  }, [
    isStrategyCreationMode,
    isCreatingStrategy,
    hasStrategyBeenCreated,
    savePlan,
  ]);

  // Preload components to prevent lazy loading delays
  useEffect(() => {
    // Only preload when we have data to show (avoid unnecessary loading)
    if (hasStrategyBeenCreated && finalProgramSequence?.length > 0) {
      // Preload all view components in the background
      const preloadComponents = async () => {
        try {
          await Promise.all([
            import("./CalendarView"),
            import("./StepByStepView"),
            import("./SlickView"),
          ]);
          console.log("Components preloaded successfully");
        } catch (error) {
          console.warn("Some components failed to preload:", error);
        }
      };

      // Small delay to avoid blocking main render
      setTimeout(preloadComponents, 100);
    }
  }, [hasStrategyBeenCreated, finalProgramSequence?.length]);

  // Listen for deletion of the current plan
  useEffect(() => {
    if (currentPlanDeleted) {
      alert("This plan has been deleted.");
      navigate("/");
      setCurrentPlanDeleted(false);
    }
  }, [currentPlanDeleted, navigate, setCurrentPlanDeleted]);

  // FIX #25/#26: Listen for feature enablement and switch to the new view
  useEffect(() => {
    const handlePlanDataUpdated = (event) => {
      const { featureEnabled, planId: eventPlanId } = event.detail || {};

      if (featureEnabled && eventPlanId === currentPlanId) {
        console.log(
          `🎯 Feature enabled event received: ${featureEnabled} for plan ${eventPlanId}`,
        );

        // Update enabled features state to include the new feature
        setEnabledFeatures((prev) => {
          const current = Array.isArray(prev) ? prev : [];
          if (!current.includes(featureEnabled)) {
            return [...current, featureEnabled];
          }
          return current;
        });

        // Map feature IDs to view keys (config.id from Core Features Registry)
        const featureToViewMap = {
          step_by_step_view: "stepByStep",
          calendar_view: "calendar",
          readme_view: "readme",
          slick_view: "slick",
          sow_view: "sow",
          strategy_view: "strategy",
        competitor_analysis_view: "competitor",
        design_authority: "designAuthority",
        };

        const viewToSwitch = featureToViewMap[featureEnabled] || featureEnabled;

        // FIX #26: CRITICAL - Update uiConfig.enabledFeatures AND views so navigation tabs render correctly
        setUIConfig((prev) => {
          if (!prev) return prev;
          const currentFeatures = Array.isArray(prev.enabledFeatures)
            ? prev.enabledFeatures
            : [];
          const currentViews = Array.isArray(prev.views) ? prev.views : [];

          // Only update if feature not already present
          const needsFeatureUpdate = !currentFeatures.includes(featureEnabled);
          const needsViewUpdate = !currentViews.includes(viewToSwitch);

          if (!needsFeatureUpdate && !needsViewUpdate) return prev;

          console.log(
            `🎯 Updating uiConfig: enabledFeatures +${featureEnabled}, views +${viewToSwitch}`,
          );
          return {
            ...prev,
            enabledFeatures: needsFeatureUpdate
              ? [...new Set([...currentFeatures, featureEnabled])]
              : currentFeatures,
            views: needsViewUpdate
              ? [...new Set([...currentViews, viewToSwitch])]
              : currentViews,
          };
        });

        // Switch to the newly enabled view
        console.log(
          `🎯 Switching to view: ${viewToSwitch} (tab should show as active)`,
        );
        setCurrentView(viewToSwitch);
      }
    };

    window.addEventListener("planDataUpdated", handlePlanDataUpdated);

    return () => {
      window.removeEventListener("planDataUpdated", handlePlanDataUpdated);
    };
  }, [currentPlanId, setEnabledFeatures, setCurrentView, setUIConfig]);

  // Create state setters object to pass to the hook
  const stateSetters = useMemo(
    () => ({
      setCurrentDesignChallenge,
      setPlanName,
      setShortSummary,
      setProblemStatement,
      setGoalStatement,
      setTargetStatement,
      setObjectivesStatement,
      setDeliverablesStatement,
      setMetricsStatement,
      setStakeholdersCustomersInvolvement,
      setExperienceDesignPrinciples,
      setSelectedPrograms,
      setFinalProgramSequence,
      setTotalDuration,
      setEnabledFeatures,
      setStrategyLifecycleStatus,
      setUIConfig, // FIX #28: Add setUIConfig so plan loader can update uiConfig.enabledFeatures for navigation tabs
      setCurrentWorkflowId: setPlanStateWorkflowId, // For workflow restoration on reload
      setIsSaved,
      setHasStrategyBeenCreated,
      setPlanState,
      setPlanLoadInitialized,
      setIsInitialLoad: (value) => {
        isInitialLoad.current = value;
      },
    }),
    [],
  ); // Empty dependency array since these are from hooks and don't change

  // Use the custom hook for plan data loading with state setters
  const { isLoading: planDataLoading } = usePlanDataLoader(
    currentPlanId,
    isCreatingStrategy,
    isStrategyCreationMode,
    planState,
    planLoadInitialized,
    hasStrategyBeenCreated,
    shortSummary,
    finalProgramSequence,
    location,
    stateSetters,
  );

  // Expose save function globally for components that need immediate saves
  useEffect(() => {
    window.forceSavePlan = () => {
      if (!isSaving && planState !== "saving" && planState !== "loading") {
        console.log("Force save triggered from component");
        savePlan();
      } else {
        console.log("Force save blocked - already saving or loading");
      }
    };

    return () => {
      delete window.forceSavePlan;
    };
  }, [savePlan, isSaving, planState]);

  // Update the main isLoading state to include plan data loading
  const combinedIsLoading = isLoading || planDataLoading;

  // Wait for plan loading to finish so NavBar's Jobs listener survives the
  // loading screen. Its first render can disappear while the plan is fetched.
  useEffect(() => {
    if (searchParams.get('jobs') !== 'open' || combinedIsLoading ||
        !planLoadInitialized || !userDetails?.id || isCreatingStrategy) return;
    window.dispatchEvent(new CustomEvent('pabs:openJobs'));
    const params = new URLSearchParams(searchParams);
    params.delete('jobs');
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash },
      { replace: true, state: location.state });
  }, [searchParams, combinedIsLoading, planLoadInitialized, userDetails?.id,
    isCreatingStrategy, navigate, location.pathname, location.hash, location.state]);

  // Debug loading states
  useEffect(() => {
    console.log("Loading states:", {
      isLoading,
      planDataLoading,
      combinedIsLoading,
      isCreatingStrategy,
      hasStrategyBeenCreated,
      planState,
    });
  }, [
    isLoading,
    planDataLoading,
    combinedIsLoading,
    isCreatingStrategy,
    hasStrategyBeenCreated,
    planState,
  ]);

  // Debug state changes for tool duration updates with detailed logging
  useEffect(() => {
    console.log("📊 PlanPage state update:", {
      finalProgramSequenceLength: finalProgramSequence?.length,
      totalDuration: totalDuration,
      firstProgramDuration: finalProgramSequence?.[0]?.totalProgramLength,
      secondProgramDuration: finalProgramSequence?.[1]?.totalProgramLength,
      programNames: Array.isArray(finalProgramSequence)
        ? finalProgramSequence.map((p) => p?.program_of_work).slice(0, 3)
        : [],
      timestamp: new Date().toISOString(),
    });

    // Log detailed tool information for debugging
    if (
      Array.isArray(finalProgramSequence) &&
      finalProgramSequence.length > 0
    ) {
      finalProgramSequence.forEach((program, index) => {
        if (program && program.toolsByStage) {
          Object.entries(program.toolsByStage).forEach(
            ([stageName, stageData]) => {
              if (
                stageData &&
                Array.isArray(stageData.tools) &&
                stageData.tools.length > 0
              ) {
                console.log(
                  `📊 Program[${index}].${stageName} tools:`,
                  stageData.tools
                    .map((tool) => `${tool?.tool}: ${tool?.duration}h`)
                    .join(", "),
                );
              }
            },
          );
        }
      });
    }
  }, [finalProgramSequence, totalDuration]);

  // Additional effect to track selectedPrograms changes
  useEffect(() => {
    console.log("📊 selectedPrograms state update:", {
      length: selectedPrograms?.length,
      firstProgramDuration: selectedPrograms?.[0]?.totalProgramLength,
      timestamp: new Date().toISOString(),
    });
  }, [selectedPrograms]);

  // Force re-render when state updates don't trigger automatically
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  useEffect(() => {
    // Force component update every time finalProgramSequence changes
    setForceUpdateKey((prev) => prev + 1);
  }, [finalProgramSequence, totalDuration]);

  // Additional effect to handle AI action state updates
  useEffect(() => {
    // Listen for component action completions that require UI refresh
    const handleActionComplete = () => {
      console.log("🔄 Forcing UI refresh after component action");
      setForceUpdateKey((prev) => prev + 1);
    };

    window.addEventListener("aiActionComplete", handleActionComplete);
    return () =>
      window.removeEventListener("aiActionComplete", handleActionComplete);
  }, []);

  // Initialize ComponentActionHandler
  const componentActionHandler = useMemo(() => {
    const stateSetters = {
      setPlanName,
      setShortSummary,
      setProblemStatement,
      setGoalStatement,
      setTargetStatement,
      setObjectivesStatement,
      setDeliverablesStatement,
      setMetricsStatement,
      setStakeholdersCustomersInvolvement,
      setExperienceDesignPrinciples,
      setSelectedPrograms,
      setFinalProgramSequence,
      setTotalDuration,
      setEnabledFeatures,
      setUIConfig,
      setCurrentWorkflowId: setPlanStateWorkflowId,
      setHasStrategyBeenCreated,
      setIsCreatingStrategy,
      setIsStrategyCreationMode,
      setIsNewPlan,
      setCurrentPlanId,
      setIsSaved,
      setPlanState,
      setPlanLoadInitialized,
      setCurrentDesignChallenge,
      setIsInitialLoad: (value) => {
        isInitialLoad.current = value;
      },
      incrementStrategyVersion: () => setStrategyVersion((v) => v + 1),
      setPendingFeatureWorkflowTrigger,
      // chatPanelKey keeps the creation chat mounted on the saved plan's URL.
      // It is not _cacheKey, which would signal a new launch on that page.
      navigateToSavedPlan:(id)=>{
        creationChatHandoffRef.current = true;
        navigate(`/plan/${id}`,{replace:true,state:{fromStrategyCreation:true,chatPanelKey:window.history.state?.usr?._cacheKey||window.history.state?.usr?.chatPanelKey}});
      },
    };

    const stateGetters = {
      finalProgramSequence,
      selectedPrograms,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      totalDuration,
      currentDesignChallenge,
      userDetails,
      currentPlanId,
      enabledFeatures,
    };

    return new ComponentActionHandler(stateSetters, stateGetters, savePlan);
  }, [
    navigate,
    setPlanName,
    setShortSummary,
    setProblemStatement,
    setGoalStatement,
    setTargetStatement,
    setObjectivesStatement,
    setDeliverablesStatement,
    setMetricsStatement,
    setStakeholdersCustomersInvolvement,
    setExperienceDesignPrinciples,
    setSelectedPrograms,
    setFinalProgramSequence,
    setTotalDuration,
    setEnabledFeatures,
    setUIConfig,
    setStrategyVersion,
    setHasStrategyBeenCreated,
    setIsCreatingStrategy,
    setIsStrategyCreationMode,
    setIsNewPlan,
    setCurrentPlanId,
    setIsSaved,
    setPlanState,
    setPlanLoadInitialized,
    setCurrentDesignChallenge,
    finalProgramSequence,
    selectedPrograms,
    shortSummary,
    goalStatement,
    problemStatement,
    targetStatement,
    objectivesStatement,
    deliverablesStatement,
    metricsStatement,
    stakeholdersCustomersInvolvement,
    totalDuration,
    currentDesignChallenge,
    userDetails,
    currentPlanId,
    enabledFeatures,
    savePlan,
  ]);

  // Handle component actions from chat - now delegates to service
  const handleComponentAction = useCallback(
    async (actionType, component, data) => {
      return await componentActionHandler.handleAction(
        actionType,
        component,
        data,
      );
    },
    [componentActionHandler],
  );

  // Helper function to format duration
  const formatDuration = useCallback((hours) => {
    if (!hours || hours === 0) return "0 hours";

    if (hours < 8) {
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    } else if (hours < 40) {
      const days = Math.floor(hours / 8);
      const remainingHours = hours % 8;
      let result = `${days} day${days !== 1 ? "s" : ""}`;
      if (remainingHours > 0) {
        result += ` ${remainingHours} hour${remainingHours !== 1 ? "s" : ""}`;
      }
      return result;
    } else {
      const weeks = Math.floor(hours / 40);
      const remainingHours = hours % 40;
      let result = `${weeks} week${weeks !== 1 ? "s" : ""}`;
      if (remainingHours > 0) {
        const days = Math.floor(remainingHours / 8);
        const remainingHoursAfterDays = remainingHours % 8;
        if (days > 0) {
          result += ` ${days} day${days !== 1 ? "s" : ""}`;
        }
        if (remainingHoursAfterDays > 0) {
          result += ` ${remainingHoursAfterDays} hour${remainingHoursAfterDays !== 1 ? "s" : ""}`;
        }
      }
      return result;
    }
  }, []);

  // Function to handle program deletion
  const handleDeleteProgram = useCallback(
    (programToRemove) => {
      console.log("Deleting program:", programToRemove);

      // Handle both program object and program index
      let programName;
      if (
        typeof programToRemove === "object" &&
        programToRemove.program_of_work
      ) {
        programName = programToRemove.program_of_work;
      } else if (typeof programToRemove === "number") {
        // Handle index-based deletion
        const program = finalProgramSequence[programToRemove];
        if (program) {
          programName = program.program_of_work;
        } else {
          console.error("Program not found at index:", programToRemove);
          return;
        }
      } else {
        console.error(
          "Invalid program parameter for deletion:",
          programToRemove,
        );
        return;
      }

      // Prevent removal of Project Preparation
      if (programName === "Project Preparation") {
        alert(
          "The Project Preparation program cannot be removed from your plan.",
        );
        return;
      }

      // Confirmation is now handled by the calling component (StepByStepView modal)

      const updatedSequence = finalProgramSequence.filter(
        (program) => program.program_of_work !== programName,
      );

      const updatedSelected = selectedPrograms.filter(
        (program) => program.program_of_work !== programName,
      );

      // Recalculate total duration
      const newTotalDuration = updatedSequence.reduce((sum, program) => {
        return sum + (program.totalProgramLength || program.duration || 0);
      }, 0);

      setFinalProgramSequence(updatedSequence);
      setSelectedPrograms(updatedSelected);
      setTotalDuration(newTotalDuration);
      setIsSaved(false);

      console.log(`Program "${programName}" removed successfully`);

      // Force save after critical action like program deletion
      setTimeout(() => {
        if (!isSaving && planState !== "saving") {
          console.log("🔥 Force saving after program deletion");
          savePlan();
        }
      }, 1000);
    },
    [
      finalProgramSequence,
      selectedPrograms,
      setFinalProgramSequence,
      setSelectedPrograms,
      setTotalDuration,
      setIsSaved,
      isSaving,
      planState,
      savePlan,
    ],
  );

  // Function to handle adding programs
  const handleAddProgramClick = useCallback(
    (insertIndex = null) => {
      const position =
        insertIndex !== null ? insertIndex : finalProgramSequence.length;
      setInsertPosition(position);
      onAddProgramModalOpen();
    },
    [finalProgramSequence.length],
  );

  // Function to handle program field changes
  const handleProgramFieldChange = useCallback(
    (programIndex, field, value) => {
      console.log(`Updating program ${programIndex}, field ${field}:`, value);

      const updatedSequence = [...finalProgramSequence];
      if (updatedSequence[programIndex]) {
        // Handle stakeholdersInvolvement field to store as string (like goals and deliverables)
        let processedValue = value;
        if (field === "stakeholdersInvolvement" && typeof value === "string") {
          processedValue = value; // Keep as string to match goals/deliverables behavior
        }

        updatedSequence[programIndex] = {
          ...updatedSequence[programIndex],
          [field]: processedValue,
        };

        // Recalculate duration if it's a duration field
        if (field === "totalProgramLength" || field === "duration") {
          const newTotalDuration = updatedSequence.reduce((sum, program) => {
            return sum + (program.totalProgramLength || program.duration || 0);
          }, 0);
          setTotalDuration(newTotalDuration);
        }

        setFinalProgramSequence(updatedSequence);
        setSelectedPrograms(updatedSequence);
        setIsSaved(false);

        console.log(
          `Program field ${field} updated, plan marked as unsaved - auto-save will trigger`,
        );
      }
    },
    [
      finalProgramSequence,
      setFinalProgramSequence,
      setSelectedPrograms,
      setTotalDuration,
      setIsSaved,
    ],
  );

  // Function to handle duration changes
  const handleDurationChange = useCallback(
    (programIndex, newDuration) => {
      handleProgramFieldChange(programIndex, "totalProgramLength", newDuration);
    },
    [handleProgramFieldChange],
  );

  // Function to handle tool removal
  const handleRemoveTool = useCallback(
    (programIndex, stageName, toolIndex) => {
      console.log(
        `Removing tool from program ${programIndex}, stage ${stageName}, tool ${toolIndex}`,
      );

      const updatedSequence = [...finalProgramSequence];
      const program = updatedSequence[programIndex];

      if (program && program.toolsByStage && program.toolsByStage[stageName]) {
        const updatedTools = [...program.toolsByStage[stageName].tools];
        updatedTools.splice(toolIndex, 1);

        updatedSequence[programIndex] = {
          ...program,
          toolsByStage: {
            ...program.toolsByStage,
            [stageName]: {
              ...program.toolsByStage[stageName],
              tools: updatedTools,
            },
          },
        };

        setFinalProgramSequence(updatedSequence);
        setIsSaved(false);
      }
    },
    [finalProgramSequence, setFinalProgramSequence, setIsSaved],
  );

  // Function to handle adding tools
  const handleAddToolClick = useCallback(
    (stageName, programIndex) => {
      console.log(`Adding tool to program ${programIndex}, stage ${stageName}`);
      const program = finalProgramSequence[programIndex];
      console.log("Selected program for tools:", program);
      console.log("Available tools count:", tools?.length || 0);

      // Ensure we have valid program data
      if (!program) {
        console.error("No program found at index", programIndex);
        return;
      }

      setSelectedProgramForTools({
        programIndex,
        stageName,
        program: {
          ...program,
          selectedStage: stageName, // Add selectedStage to the program object
        },
      });
      setIsToolSelectionModalOpen(true);
    },
    [finalProgramSequence, tools],
  );

  // Function to handle activity card clicks
  const handleActivityCardClick = useCallback((activityData) => {
    console.log("Activity card clicked:", activityData);
    // Handle both old format (just toolId) and new format (object with toolId)
    const toolData =
      typeof activityData === "object"
        ? activityData
        : { toolId: activityData };
    console.log("Processed tool data:", toolData);
    setSelectedActivityData(toolData);
    setIsActivityDetailPanelOpen(true);
  }, []);

  // Function to generate fields with AI
  const handleGenerateField = useCallback(
    async (field, programIndex, program) => {
      console.log(`Generating ${field} for program ${programIndex}`);

      // Set loading state
      setLoadingState((prev) => ({
        ...prev,
        [`${programIndex}-${field}`]: true,
      }));

      try {
        const targetProgram = program || finalProgramSequence[programIndex];
        if (!targetProgram) return;

        let endpoint = "";
        let requestData = {};

        // Determine endpoint and data based on field
        switch (field) {
          case "goal":
            endpoint = "/api/ai/generate-goal";
            requestData = {
              designChallenge: currentDesignChallenge,
              programName: targetProgram.program_of_work,
              programDescription: targetProgram.description,
              programOutcome: targetProgram.goal,
              tools: targetProgram.toolsByStage
                ? Object.values(targetProgram.toolsByStage).flatMap((stage) =>
                    stage.tools.map((tool) => tool.tool).join(", "),
                  )
                : [],
              problemStatement: problemStatement,
              finalProgramSequence: finalProgramSequence,
            };
            break;

          case "expectedDeliverables":
            endpoint = "/api/ai/generate-expectedDeliverables";
            requestData = {
              designChallenge: currentDesignChallenge,
              programName: targetProgram.program_of_work,
              tools: targetProgram.toolsByStage
                ? Object.values(targetProgram.toolsByStage).flatMap(
                    (stage) => stage.tools,
                  )
                : [],
              problemStatement: problemStatement,
            };
            break;

          case "stakeholdersInvolvement":
            endpoint = "/api/ai/generate-stakeholdersInvolvement";
            requestData = {
              designChallenge: currentDesignChallenge,
              programName: targetProgram.program_of_work,
              tools: targetProgram.toolsByStage
                ? Object.values(targetProgram.toolsByStage).flatMap(
                    (stage) => stage.tools,
                  )
                : [],
              problemStatement: problemStatement,
              duration: targetProgram.totalProgramLength || 0,
            };
            break;

          default:
            throw new Error(`Unknown field: ${field}`);
        }

        console.log(
          `Making API call to ${endpoint} for ${field}:`,
          requestData,
        );

        const response = await fetchWithAuth(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });

        if (response.ok) {
          const result = await response.json();
          console.log(`API response for ${field}:`, result);

          // Update the appropriate field based on response
          let updatedValue = null;
          switch (field) {
            case "goal":
              if (result.objectives) {
                updatedValue = result.objectives;
                handleProgramFieldChange(
                  programIndex,
                  "goal",
                  result.objectives,
                );
              }
              break;
            case "expectedDeliverables":
              if (result.deliverables) {
                updatedValue = result.deliverables;
                handleProgramFieldChange(
                  programIndex,
                  "expectedDeliverables",
                  result.deliverables,
                );
              }
              break;
            case "stakeholdersInvolvement":
              if (result.stakeholdersInvolvement) {
                // Handle both string and array responses for stakeholders
                const stakeholdersData = Array.isArray(
                  result.stakeholdersInvolvement,
                )
                  ? result.stakeholdersInvolvement.join("\n")
                  : result.stakeholdersInvolvement;

                updatedValue = stakeholdersData;
                console.log(
                  `Setting stakeholders for program ${programIndex}:`,
                  stakeholdersData,
                );
                handleProgramFieldChange(
                  programIndex,
                  "stakeholdersInvolvement",
                  stakeholdersData,
                );
              }
              break;
          }

          // Force save after successful generation to ensure data persistence
          if (updatedValue) {
            console.log(
              `Generated ${field} for program ${programIndex}, value:`,
              updatedValue,
            );
            setIsSaved(false); // Force unsaved state

            // Trigger immediate save after a longer delay to ensure state has fully updated
            setTimeout(() => {
              if (!isSaving && planState !== "saving") {
                console.log(
                  `Executing immediate save after ${field} generation`,
                );
                // Double-check the program has the updated value before saving
                const updatedProgram = finalProgramSequence[programIndex];
                if (updatedProgram && updatedProgram[field]) {
                  console.log(
                    `Confirmed program has updated ${field}:`,
                    updatedProgram[field],
                  );
                }
                savePlan();
              }
            }, 1000); // Increased delay to 1 second
          }
        } else {
          console.error(`Failed to generate ${field}:`, response.statusText);
        }
      } catch (error) {
        console.error(`Error generating ${field}:`, error);
      } finally {
        // Clear loading state
        setLoadingState((prev) => ({
          ...prev,
          [`${programIndex}-${field}`]: false,
        }));
      }
    },
    [
      finalProgramSequence,
      currentDesignChallenge,
      problemStatement,
      handleProgramFieldChange,
      setIsSaved,
      isSaving,
      planState,
      savePlan,
    ],
  );

  const addProgramToSequence = (program) => {
    try {
      console.log(
        "addProgramToSequence called with program:",
        program?.ProgramName || program?.program_of_work,
      );

      // Extract program name properly from multiple possible fields
      const programName =
        program.ProgramName ||
        program.program_of_work ||
        program.name ||
        program.title ||
        "Unknown Program";

      // Extract program methods using consistent logic
      let programMethods = [];
      if (Array.isArray(program.Methods)) {
        programMethods = program.Methods;
      } else if (typeof program.Methods === "string") {
        programMethods = program.Methods.split(",").map((m) => m.trim());
      } else if (Array.isArray(program.related_methods)) {
        programMethods = program.related_methods;
      } else if (typeof program.related_methods === "string") {
        programMethods = program.related_methods
          .split(",")
          .map((m) => m.trim());
      } else if (Array.isArray(program.methods)) {
        programMethods = program.methods;
      } else if (typeof program.methods === "string") {
        programMethods = program.methods.split(",").map((m) => m.trim());
      } else {
        // Default methods based on program type
        programMethods = ["Design Thinking"];
      }

      // CRITICAL FIX: Extract program stages properly from database data
      let programStages = [];

      // First try to get stages from the Stages field (primary source from database)
      if (Array.isArray(program.Stages) && program.Stages.length > 0) {
        programStages = program.Stages;
      } else if (typeof program.Stages === "string" && program.Stages.trim()) {
        programStages = program.Stages.split(",")
          .map((stage) => stage.trim())
          .filter(Boolean);
      }
      // Try lowercase stages field
      else if (Array.isArray(program.stages) && program.stages.length > 0) {
        programStages = program.stages;
      } else if (typeof program.stages === "string" && program.stages.trim()) {
        programStages = program.stages
          .split(",")
          .map((stage) => stage.trim())
          .filter(Boolean);
      }
      // Try related_stages field (another possible field name)
      else if (
        Array.isArray(program.related_stages) &&
        program.related_stages.length > 0
      ) {
        programStages = program.related_stages;
      } else if (
        typeof program.related_stages === "string" &&
        program.related_stages.trim()
      ) {
        programStages = program.related_stages
          .split(",")
          .map((stage) => stage.trim())
          .filter(Boolean);
      }
      // Use the program's phase as a single stage only if no stages found
      else if (program.phase || program.Phase) {
        programStages = [program.phase || program.Phase];
      }
      // LAST RESORT: Try to infer from tools' stages if program has tools
      else if (
        program.toolsByStage &&
        Object.keys(program.toolsByStage).length > 0
      ) {
        programStages = Object.keys(program.toolsByStage);
      }
      // FINAL FALLBACK: Only use hardcoded values if absolutely no data exists
      else {
        console.warn(
          `No stages found for program ${programName}, using fallback based on name`,
        );
        if (programName.toLowerCase().includes("sprint")) {
          programStages = ["Define", "Ideate", "Prototype", "Test"];
        } else if (programName.toLowerCase().includes("discovery")) {
          programStages = ["Research", "Analysis"];
        } else if (programName.toLowerCase().includes("implementation")) {
          programStages = ["Development", "Deployment"];
        } else {
          programStages = ["Implementation"]; // Changed from Discovery to Implementation as more generic
        }
      }

      console.log(
        `Processing ${programName} with methods:`,
        programMethods,
        "and stages:",
        programStages,
      );

      // Validate tools availability
      if (!tools || !Array.isArray(tools) || tools.length === 0) {
        console.warn(
          "No tools available - creating program with empty toolsByStage",
        );
        return createFallbackProgram(
          program,
          programName,
          programMethods,
          programStages,
        );
      }

      // Import the needed functions
      const {
        detectComplexity,
        detectUrgency,
      } = require("./generateActivityPlan.js");

      // USE EXISTING PROVEN FUNCTIONS from generateActivityPlan.js
      console.log(`Using filterRelevantTools for ${programName}`);
      const relevantTools = filterRelevantTools(
        tools,
        currentDesignChallenge || "",
        programStages,
        programMethods,
      );

      console.log(
        `Found ${relevantTools.length} relevant tools for ${programName}`,
      );

      // USE EXISTING complexity/urgency detection and tool duration calculation
      const complexityScore = detectComplexity(currentDesignChallenge || "");
      const urgencyMultiplier = detectUrgency(currentDesignChallenge || "");

      relevantTools.forEach((tool) => {
        tool.duration = determineToolDuration(
          tool,
          complexityScore,
          urgencyMultiplier,
        );
      });

      // USE EXISTING stage organization function
      const toolsByStage = calculateProgramLengthAndStages(relevantTools);

      // Calculate total duration from tools or use original
      const calculatedDuration = Object.values(toolsByStage).reduce(
        (total, stage) => total + (stage.totalDuration || 0),
        0,
      );
      const originalDuration =
        parseInt(program.duration) ||
        parseInt(program.totalProgramLength) ||
        40;
      const finalDuration =
        calculatedDuration > 0 ? calculatedDuration : originalDuration;

      // Create processed program with all the proven logic
      const processedProgram = {
        ...program,
        program_of_work: programName,
        phase: program.phase || program.Phase || program.phases || "Discovery", // Fixed: Use actual phase from program
        goal:
          program.goal ||
          program.outcome ||
          program.description ||
          `Execute ${programName} to achieve project objectives`,
        description:
          program.description ||
          program.Description ||
          `A comprehensive ${programName} program`,
        methods: programMethods,
        Stages: programStages,
        focus: Array.isArray(program.Focus) ? program.Focus : [],
        expectedDeliverables: Array.isArray(program.expectedDeliverables)
          ? program.expectedDeliverables
          : [],
        stakeholdersInvolvement: program.stakeholdersInvolvement || "",
        toolsByStage:
          Object.keys(toolsByStage).length > 0
            ? toolsByStage
            : createEmptyStages(programStages, originalDuration),
        totalProgramLength: finalDuration,
        isIncluded: true,
        score: program.score || 0,
        similarity_score: program.similarity_score || 0,
      };

      console.log(`Processed ${programName}:`, {
        duration: processedProgram.totalProgramLength,
        toolStages: Object.keys(processedProgram.toolsByStage).length,
        toolsCount: relevantTools.length,
        stages: processedProgram.Stages,
        toolsByStageKeys: Object.keys(processedProgram.toolsByStage),
      });

      return processedProgram;
    } catch (error) {
      console.error("Error in addProgramToSequence:", error);
      return createFallbackProgram(
        program,
        program?.ProgramName || "Unknown Program",
        ["Design Thinking"],
        ["Discovery"],
      );
    }
  };

  // Helper function to create empty stages structure
  const createEmptyStages = (stages, duration) => {
    const durationPerStage = Math.floor(duration / stages.length) || 1;
    return stages.reduce((acc, stage) => {
      acc[stage] = { tools: [], totalDuration: durationPerStage };
      return acc;
    }, {});
  };

  // Helper function to create fallback program
  const createFallbackProgram = (program, programName, methods, stages) => {
    const duration = parseInt(program?.duration) || 40;

    // Better program name extraction for fallback
    const fallbackName =
      programName !== "Unknown Program"
        ? programName
        : program?.ProgramName ||
          program?.program_of_work ||
          program?.name ||
          "New Program";

    // Better phase extraction for fallback
    const fallbackPhase =
      program?.phase ||
      program?.Phase ||
      program?.phases ||
      (fallbackName.toLowerCase().includes("sprint")
        ? "Discovery"
        : fallbackName.toLowerCase().includes("implementation")
          ? "Implementation"
          : fallbackName.toLowerCase().includes("design")
            ? "Design"
            : "Discovery");

    return {
      program_of_work: fallbackName,
      phase: fallbackPhase,
      goal:
        program?.goal ||
        program?.outcome ||
        `Execute ${fallbackName} to achieve project objectives`,
      description:
        program?.description ||
        program?.Description ||
        `A comprehensive ${fallbackName} program`,
      totalProgramLength: duration,
      isIncluded: true,
      methods: methods,
      Stages: stages,
      toolsByStage: createEmptyStages(stages, duration),
      expectedDeliverables: [],
      stakeholdersInvolvement: "",
      score: 0,
      similarity_score: 0,
    };
  };

  const handleGenerateFullPlanField = useCallback(
    async (fieldName, additionalData = {}) => {
      console.log(
        "handleGenerateFullPlanField called for " + fieldName + " with data:",
        additionalData,
      );

      const loadingKey = "fullPlan-" + fieldName;
      setLoadingState((prev) => ({
        ...prev,
        [loadingKey]: true,
      }));

      try {
        let endpoint = "";
        let requestData = {};

        // Determine endpoint and data based on fieldName
        switch (fieldName) {
          case "deliverablesRecommendation":
            endpoint = "/api/ai/generate-deliverablesRecommendation";
            requestData = {
              designChallenge: additionalData.designChallenge || shortSummary,
              shortSummary: additionalData.shortSummary || shortSummary,
              goalStatement: additionalData.goalStatement || goalStatement,
              finalProgramSequence:
                additionalData.finalProgramSequence || finalProgramSequence,
            };
            break;

          case "totalstakeholdersInvolvement":
            endpoint = "/api/ai/generate-totalstakeholdersInvolvement";
            requestData = {
              designChallenge: additionalData.designChallenge || shortSummary,
              shortSummary: additionalData.shortSummary || shortSummary,
              programName: additionalData.programName || "Full Strategy",
              targetStatement:
                additionalData.targetStatement || targetStatement,
              programs: finalProgramSequence || [],
            };
            break;

          case "metricsRecommendation":
            endpoint = "/api/ai/generate-metricsRecommendation";
            requestData = {
              designChallenge: additionalData.designChallenge || shortSummary,
              shortSummary: additionalData.shortSummary || shortSummary,
              goalStatement: additionalData.goalStatement || goalStatement,
            };
            break;

          case "experienceDesignPrinciples":
            endpoint = "/api/ai/generate-experienceDesignPrinciples";
            requestData = {
              designChallenge: additionalData.designChallenge || shortSummary,
              shortSummary: additionalData.shortSummary || shortSummary,
              goalStatement: additionalData.goalStatement || goalStatement,
              targetStatement:
                additionalData.targetStatement || targetStatement,
            };
            break;

          default:
            throw new Error("Unknown field: " + fieldName);
        }

        console.log(
          "Making API call to " + endpoint + " with data:",
          requestData,
        );

        const response = await fetchWithAuth(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            "API call failed: " + response.status + " - " + errorText,
          );
        }

        const result = await response.json();
        console.log("API response for " + fieldName + ":", result);

        // Update the appropriate state based on fieldName
        switch (fieldName) {
          case "deliverablesRecommendation":
            if (result.deliverables_recommendation || result.deliverables) {
              setDeliverablesStatement(
                result.deliverables_recommendation || result.deliverables,
              );
              setIsSaved(false);
            }
            break;

          case "totalstakeholdersInvolvement":
            if (
              result.stakeholders_involvement ||
              result.stakeholdersInvolvement
            ) {
              const stakeholdersData =
                result.stakeholders_involvement ||
                result.stakeholdersInvolvement;
              // Handle both string and array responses
              const stakeholdersText = Array.isArray(stakeholdersData)
                ? stakeholdersData.join("\n")
                : stakeholdersData;
              setStakeholdersCustomersInvolvement(stakeholdersText);
              setIsSaved(false);
            }
            break;

          case "metricsRecommendation":
            if (result.metrics_recommendation || result.metrics) {
              setMetricsStatement(
                result.metrics_recommendation || result.metrics,
              );
              setIsSaved(false);
            }
            break;

          case "experienceDesignPrinciples":
            if (result.experience_design_principles || result.principles) {
              setExperienceDesignPrinciples(
                result.experience_design_principles || result.principles,
              );
              setIsSaved(false);
            }
            break;
        }
      } catch (error) {
        console.error(
          "Error in handleGenerateFullPlanField for " + fieldName + ":",
          error,
        );
        // You might want to show a user-friendly error message here
      } finally {
        setLoadingState((prev) => ({
          ...prev,
          [loadingKey]: false,
        }));
      }
    },
    [
      currentDesignChallenge,
      finalProgramSequence,
      goalStatement,
      setIsSaved,
      setDeliverablesStatement,
      setMetricsStatement,
      setStakeholdersCustomersInvolvement,
      setExperienceDesignPrinciples,
      shortSummary,
      targetStatement,
    ],
  );

  // Additional missing functions from original implementation

  const handleProgramSelection = (programId) => {
    setSelectedProgramIds((prevIds) => {
      if (prevIds.includes(programId)) {
        return prevIds.filter((id) => id !== programId);
      } else {
        return [...prevIds, programId];
      }
    });
  };

  const handleAddPrograms = () => {
    console.log("Adding programs:", selectedProgramIds);
    console.log("Available programs:", programsOfWork?.length || 0);

    if (!selectedProgramIds || selectedProgramIds.length === 0) {
      console.warn("No programs selected for addition");
      onAddProgramModalClose();
      return;
    }

    try {
      // Find the programs to add from the available programs
      // Use same identifier logic as AddProgramModal: id || program_of_work || ProgramName
      const programsToAdd = programsOfWork.filter((program) => {
        const programId = program.id || program.program_of_work || program.ProgramName;
        const isSelected = selectedProgramIds.includes(programId);
        console.log(
          `Program ${program.ProgramName || program.program_of_work} (ID: ${programId}): ${isSelected ? "SELECTED" : "not selected"}`,
        );
        return isSelected;
      });

      console.log("Programs to add:", programsToAdd.length);

      if (programsToAdd.length === 0) {
        console.error("No valid programs found for selected IDs");
        onAddProgramModalClose();
        return;
      }

      // Process each program using the corrected addProgramToSequence function
      const newPrograms = programsToAdd
        .map((program) => {
          console.log(
            "Processing program:",
            program.ProgramName || program.program_of_work,
          );

          try {
            const processedProgram = addProgramToSequence(program);

            console.log("Processed program result:", {
              name: processedProgram.program_of_work,
              duration: processedProgram.totalProgramLength,
              stagesCount: Object.keys(processedProgram.toolsByStage || {})
                .length,
              totalTools: Object.values(
                processedProgram.toolsByStage || {},
              ).reduce((sum, stage) => sum + (stage.tools?.length || 0), 0),
              methods: processedProgram.methods?.length || 0,
              stages: processedProgram.Stages?.length || 0,
            });

            return processedProgram;
          } catch (error) {
            console.error(
              "Error processing program:",
              program.ProgramName,
              error,
            );
            // Return a basic program structure as fallback
            return {
              program_of_work:
                program.ProgramName ||
                program.program_of_work ||
                "Unknown Program",
              phase: program.phase || program.Phase || "Discovery",
              goal:
                program.goal ||
                program.outcome ||
                program.description ||
                "Program objective",
              description:
                program.description ||
                program.Description ||
                "Program description",
              totalProgramLength: parseInt(program.duration) || 40,
              isIncluded: true,
              methods: [],
              Stages: ["Implementation"],
              toolsByStage: {},
              expectedDeliverables: [],
              stakeholdersInvolvement: "",
              score: 0,
              similarity_score: 0,
            };
          }
        })
        .filter(Boolean); // Remove any null programs

      if (newPrograms.length === 0) {
        console.error("No programs could be processed successfully");
        onAddProgramModalClose();
        return;
      }

      // Insert programs at the correct position
      const updatedSequence =
        insertPosition !== null
          ? [
              ...finalProgramSequence.slice(0, insertPosition),
              ...newPrograms,
              ...finalProgramSequence.slice(insertPosition),
            ]
          : [...finalProgramSequence, ...newPrograms];

      console.log("Updated sequence length:", updatedSequence.length);
      console.log(
        "New programs details:",
        newPrograms.map((p) => ({
          name: p.program_of_work,
          duration: p.totalProgramLength,
          toolStages: Object.keys(p.toolsByStage || {}).length,
        })),
      );

      // Update state
      setFinalProgramSequence(updatedSequence);
      setSelectedPrograms(updatedSequence);

      // Recalculate total duration
      const newTotalDuration = updatedSequence.reduce((sum, program) => {
        return sum + (program.totalProgramLength || 0);
      }, 0);
      setTotalDuration(newTotalDuration);
      setIsSaved(false);

      console.log(
        "Programs added successfully, new total duration:",
        newTotalDuration,
      );

      // Force save after successful addition
      setTimeout(() => {
        if (!isSaving && planState !== "saving") {
          console.log("🔥 Force saving after program addition");
          savePlan();
        }
      }, 1000);
    } catch (error) {
      console.error("Error in handleAddPrograms:", error);
    } finally {
      // Clean up and close modal
      onAddProgramModalClose();
      setSelectedProgramIds([]);
      setInsertPosition(null);
    }
  };

  const confirmDeleteProgram = () => {
    if (programToDelete !== null) {
      const updatedSequence = finalProgramSequence.filter(
        (_, index) => index !== programToDelete,
      );
      setFinalProgramSequence(updatedSequence);
      setSelectedPrograms(updatedSequence);

      const newTotalDuration = updatedSequence.reduce((sum, program) => {
        return sum + (program.totalProgramLength || program.duration || 0);
      }, 0);
      setTotalDuration(newTotalDuration);

      setProgramToDelete(null);
      onDeleteAlertClose();
    }
  };

  const closeToolSelectionModal = () => {
    setIsToolSelectionModalOpen(false);
    setSelectedProgramForTools(null);
  };

  // EMERGENCY: Detect corrupted props early (moved after hooks)
  if (
    programsOfWork?.length > 1000 ||
    methods?.length > 1000 ||
    tools?.length > 1000
  ) {
    console.error(
      "EMERGENCY: Corrupted props detected, preventing render crash",
    );
    return (
      <Box
        minHeight="100vh"
        bg="gray.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Text fontSize="xl" color="red.500">
            Data Error Detected
          </Text>
          <Text>Please refresh the page or contact support.</Text>
          <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        </VStack>
      </Box>
    );
  }

  // Safety check for undefined/null components before rendering
  if (!Box || !VStack || !Text || !Spinner) {
    console.error("EMERGENCY: Core Chakra UI components not available");
    return React.createElement(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontSize: "18px",
        },
      },
      "Loading...",
    );
  }

  // Only show loading for actual plan data loading, not for new strategy creation
  if (combinedIsLoading && !isStrategyCreationMode && currentPlanId) {
    return (
      <Box
        minHeight="100vh"
        bg="gray.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading your plan...</Text>
        </VStack>
      </Box>
    );
  }

  // Early return if user is not authenticated
  if (!userDetails?.id) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading user data...</Text>
        </VStack>
      </Box>
    );
  }

  // Show strategy creation loading state
  if (isCreatingStrategy) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        bg="gray.50"
      >
        <VStack spacing={6} textAlign="center">
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <VStack spacing={2}>
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              Creating Your Strategy
            </Text>
            <Text fontSize="md" color="gray.600" maxWidth="400px">
              {strategyCreationPhrase}
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  // Show loading state for plan data
  if (isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading plan data...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      bg="gray.50"
      paddingLeft={0}
      position="relative"
      zIndex="1"
      overflow="hidden"
      css={{
        overscrollBehavior: "contain",
        minHeight: "100dvh",
        "@supports not (min-height: 100dvh)": {
          minHeight: "100vh",
        },
      }}
    >
      <Flex
        direction="row"
        position="relative"
        overflow="hidden"
        css={{
          overscrollBehavior: "contain",
          height: "100dvh",
          "@supports not (height: 100dvh)": {
            height: "100vh",
          },
        }}
      >
        {/* Chat Panel - Left Side */}
        <Box
          width={{
            base: "0px", // Always 0 on mobile — EmbeddedChatPanel overlays as position:fixed
            md: isNarrativeCollapsed ? "0px" : `${leftPanelWidth}px`,
          }}
          borderRightRadius={{ base: 0, md: "24px" }}
          transition={isDragging ? "none" : "width 0.3s ease"}
          overflow="hidden"
          height="100%"
          flexShrink={0}
          css={{ overscrollBehavior: "contain" }}
        >
          <Suspense fallback={<Box />}>
            {/* CRITICAL FIX: Only render chat when plan context is ready */}
            {isChatReady ? (
              <EmbeddedChatPanel
                // CRITICAL FIX: Force remount when navigating from Brief to ensure workflow effect runs
                key={
                  location.state?._cacheKey ||
                  location.state?.chatPanelKey ||
                  `plan_${currentPlanId || "new"}_${location.state?.initiateStrategyCreation ? "launch" : "view"}`
                }
                ref={chatbotRef}
                currentDesignChallenge={currentDesignChallenge}
                selectedPrograms={selectedPrograms}
                shortSummary={shortSummary}
                goalStatement={goalStatement}
                problemStatement={problemStatement}
                targetStatement={targetStatement}
                objectivesStatement={objectivesStatement}
                deliverablesStatement={deliverablesStatement}
                metricsStatement={metricsStatement}
                stakeholdersCustomersInvolvement={
                  stakeholdersCustomersInvolvement
                }
                finalProgramSequence={finalProgramSequence}
                totalDuration={totalDuration}
                handleComponentAction={handleComponentAction}
                isStrategyCreationMode={isStrategyCreationMode}
                setIsStrategyCreationMode={setIsStrategyCreationMode}
                programsOfWork={programsOfWork}
                methods={methods}
                tools={tools}
                deliverables={deliverables}
                onCollapse={(newCollapseState) => {
                  setIsNarrativeCollapsed(newCollapseState);
                  if (onChatCollapseChange)
                    onChatCollapseChange(newCollapseState);
                }}
                isNarrativeCollapsed={isNarrativeCollapsed}
                currentPlanId={currentPlanId}
                currentWorkflowId={currentWorkflowId}
                uiConfig={uiConfig}
                executionPlan={executionPlan}
                shouldAutoLaunchWorkflow={shouldAutoLaunchWorkflow}
                // Capture chatbot messages during strategy workflow
                onChatMessage={(message) => {
                  console.log("Strategy chat message:", message);
                  if (
                    chatbotRef.current &&
                    typeof chatbotRef.current.addMessage === "function"
                  ) {
                    chatbotRef.current.addMessage(message);
                  }
                  // Also add to strategy workflow conversation history
                  if (strategyWorkflowService.current) {
                    strategyWorkflowService.current.addConversationMessage(
                      message,
                    );
                  }
                }}
                onNavigateToView={setCurrentView}
                currentView={currentView}
                onOpenShareModal={onShareModalOpen}
                onOpenMcpModal={onMcpModalOpen}
                onOpenLearnings={onLearningsSidebarOpen}
                onLearningsCountChanged={(count) => {
                  if (count) setLearningsPendingCount(prev => prev + count);
                  fetchLearningsPendingCount();
                }}
              />
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                p={4}
              >
                <VStack spacing={2}>
                  <Spinner size="md" color="blue.500" />
                  <Text fontSize="sm" color="gray.600">
                    Loading chat...
                  </Text>
                </VStack>
              </Box>
            )}
          </Suspense>
        </Box>

        {/* Draggable Divider - Only show when not collapsed */}
        {!isNarrativeCollapsed && (
          <Box
            width="1px"
            height="90%"
            mt="30px"
            bg={isDragging ? "brand.500" : "none"}
            cursor="col-resize"
            onMouseDown={handleMouseDown}
            _hover={{ bg: "blue.400" }}
            transition="background-color 0.2s ease"
            position="relative"
            zIndex={10}
            flexShrink={0}
          >
            {/* Visual indicator */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width="20px"
              height="40px"
              bg={isDragging ? "blue.600" : "gray.400"}
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              opacity={0.7}
              _hover={{ opacity: 1 }}
            >
              <Text fontSize="xs" color="white" fontWeight="bold">
                ⋮⋮
              </Text>
            </Box>
          </Box>
        )}

        {/* Main Content Area - Right Side */}
        <Box
          ref={setScrollContainerRef}
          flex="1"
          minHeight="0"
          overflowY="auto"
          overflowX="hidden"
          position="relative"
          minWidth={{ base: 0, md: "300px" }}
          pb={{ base: "136px", md: "80px" }}
          css={{
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* NavBar - scrolls away normally (NOT sticky) */}
          <Box mb={4} pl={{ base: 3, md: 6 }} pr={0}>
            <NavBar
              planName={
                isStrategyCreationMode || isCreatingStrategy
                  ? "Creating Strategy..."
                  : planName ||
                    shortSummary ||
                    currentDesignChallenge ||
                    "New Plan"
              }
              hasUnsavedChanges={!isSaved}
              onSavePlan={savePlan}
              isNewPlan={isNewPlan}
              isSaving={isSaving}
              isSaved={isSaved}
              currentDesignChallenge={currentDesignChallenge}
              selectedPrograms={selectedPrograms}
              shortSummary={shortSummary}
              goalStatement={goalStatement}
              problemStatement={problemStatement}
              targetStatement={targetStatement}
              objectivesStatement={objectivesStatement}
              deliverablesStatement={deliverablesStatement}
              metricsStatement={metricsStatement}
              stakeholdersCustomersInvolvement={
                stakeholdersCustomersInvolvement
              }
              finalProgramSequence={finalProgramSequence}
              totalDuration={totalDuration}
              handleComponentAction={handleComponentAction}
              planState={planState}
              currentPlanId={currentPlanId}
              onOpenShareModal={onShareModalOpen}
              onOpenMcpModal={openMcpModal}
              onOpenLearningsPanel={onLearningsSidebarOpen}
              learningsPendingCount={learningsPendingCount}
              strategyLifecycleStatus={strategyLifecycleStatus}
              onStrategyLifecycleStatusChange={handleStrategyLifecycleStatusChange}
              onRenamePlan={!(isStrategyCreationMode || isCreatingStrategy) ? async (newName) => {
                setPlanName(newName);
                setIsSaved(false);
              } : undefined}
            />
          </Box>


          {(isStrategyCreationMode || isCreatingStrategy) &&
          !hasStrategyBeenCreated ? (
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="gray.50"
              zIndex="1000"
            >
              <VStack spacing={6}>
                <Spinner size="xl" color="blue.500" />
                <Text fontSize="md" color="gray.600" textAlign="center">
                  {strategyCreationPhrase ||
                    "Analyzing your design challenge..."}
                </Text>
              </VStack>
            </Box>
          ) : (
            <Box flex="1" minHeight="0">
              {/* Dynamic Content based on workflow configuration and current view */}
              {/* Show content when strategy workflow is finished OR when we have valid data */}
              {((hasStrategyBeenCreated &&
                !isCreatingStrategy &&
                !isStrategyCreationMode) ||
                shortSummary ||
                finalProgramSequence?.length > 0) &&
                Array.isArray(finalProgramSequence) &&
                uiConfig && (
                  <Suspense fallback={<ViewLoadingFallback />}>
                    {renderCurrentView()}
                  </Suspense>
                )}

              {/* Show message only if there's truly no data and no workflow in progress */}
              {!hasStrategyBeenCreated &&
                !isStrategyCreationMode &&
                !isCreatingStrategy &&
                (!shortSummary || finalProgramSequence?.length === 0) && (
                  <Box textAlign="center" p={10}>
                    <Text fontSize="lg" color="gray.600">
                      No strategy data available. Please create a new strategy
                      or check if the plan loaded correctly.
                    </Text>
                  </Box>
                )}
            </Box>
          )}
        </Box>
      </Flex>

      {/* Activity Detail Panel */}
      {isActivityDetailPanelOpen && (
        <Suspense fallback={<Box />}>
          <ActivityDetailPanel
            isOpen={isActivityDetailPanelOpen}
            onClose={() => {
              setIsActivityDetailPanelOpen(false);
              setSelectedActivityData(null);
            }}
            toolId={selectedActivityData?.toolId}
            designChallenge={shortSummary || currentDesignChallenge}
          />
        </Suspense>
      )}

      {/* Add Program Modal */}
      {isAddProgramModalOpen && (
        <Suspense fallback={<Box />}>
          <AddProgramModal
            isOpen={isAddProgramModalOpen}
            onClose={onAddProgramModalClose}
            availablePrograms={availablePrograms || programsOfWork || []}
            selectedProgramIds={selectedProgramIds}
            onProgramSelection={handleProgramSelection}
            onAddPrograms={handleAddPrograms}
          />
        </Suspense>
      )}

      {/* Tool Selection Modal */}
      {isToolSelectionModalOpen && (
        <Suspense fallback={<Box />}>
          <ToolSelectionModal
            isOpen={isToolSelectionModalOpen}
            onClose={closeToolSelectionModal}
            program={selectedProgramForTools?.program}
            programIndex={selectedProgramForTools?.programIndex}
            stageName={selectedProgramForTools?.stageName}
            tools={tools || []}
            onUpdateProgram={(updatedProgram, programIndex) => {
              console.log(
                "Tool selection updating program:",
                programIndex,
                updatedProgram,
              );
              const updatedSequence = [...finalProgramSequence];
              updatedSequence[programIndex] = updatedProgram;
              setFinalProgramSequence(updatedSequence);
              setSelectedPrograms(updatedSequence);

              const newTotalDuration = updatedSequence.reduce((sum, prog) => {
                return sum + (prog.totalProgramLength || prog.duration || 0);
              }, 0);
              setTotalDuration(newTotalDuration);
              setIsSaved(false);
            }}
          />
        </Suspense>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Program
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this program? This action cannot
              be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteProgram} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Share Plan Modal */}
      <Suspense fallback={null}>
        <SharePlanModal
          isOpen={isShareModalOpen}
          onClose={onShareModalClose}
          planId={currentPlanId}
          planName={planName || shortSummary || currentDesignChallenge || "Plan"}
          enabledFeatures={enabledFeatures}
        />
      </Suspense>



      {/* MCP Involve PABS Modal */}
      <Suspense fallback={null}>
        <McpModal
          isOpen={isMcpModalOpen}
          onClose={onMcpModalClose}
          currentPlanId={currentPlanId}
          planName={planName || shortSummary || currentDesignChallenge || "Plan"}
          initialTool={mcpInitialTool}
        />
      </Suspense>

      {/* Learnings Sidebar */}
      {currentPlanId && (
        <Suspense fallback={null}>
          <LearningSidebar
            isOpen={isLearningsSidebarOpen}
            initialLearningId={requestedLearningId}
            onClose={() => {
              setRequestedLearningId(null);
              onLearningsSidebarClose();
              fetchLearningsPendingCount();
            }}
            planId={currentPlanId}
            onSendChatPrompt={(message) => {
              if (chatbotRef.current?.handleSend) {
                if (isNarrativeCollapsed) setIsNarrativeCollapsed(false);
                onLearningsSidebarClose();
                setTimeout(() => chatbotRef.current.handleSend(message), 150);
              }
            }}
            onStrategyChanged={(payload) => {
              fetchLearningsPendingCount();
              const changes = Array.isArray(payload) ? payload : (payload?.changes || []);
              fetchFieldUpdates();
              setRecentlyApplied(changes);
              setStrategyResetKey(k => k + 1);
              if (changes.length) {
                refreshPlanContent();
              }
            }}
          />
        </Suspense>
      )}

      {currentPlanId && (
        <Suspense fallback={null}>
          <InProductWalkthrough
            planId={currentPlanId}
            isReady={
              (isPlanReadyWalkthroughReady ||
                !!shortSummary ||
                finalProgramSequence?.length > 0) &&
              !isCreatingStrategy &&
              !isStrategyCreationMode
            }
            onOpenSources={() => navigate("/connectors")}
            onOpenDesignAuthority={() => {
              setShowAddView(false);
              startTransition(() => setCurrentView("designAuthority"));
            }}
            onOpenJobs={() => {
              window.dispatchEvent(new CustomEvent("pabs:openJobs", {
                detail: { tab: "Tasks" },
              }));
            }}
            onLearnMore={(href) => window.open(href, "_blank", "noopener,noreferrer")}
          />
        </Suspense>
      )}

      {/* Frosted-glass gradient zone — sits below the nav pill, blurs the content behind it */}
      {uiConfig && filteredNavButtons.length > 1 &&
        ((hasStrategyBeenCreated && !isCreatingStrategy && !isStrategyCreationMode) ||
          (shortSummary && finalProgramSequence?.length > 0)) && (
        <Box
          position="fixed"
          bottom={0}
          left={{ base: 0, md: `${isNarrativeCollapsed ? 0 : leftPanelWidth}px` }}
          right={0}
          height={{ base: "140px", md: "120px" }}
          pointerEvents="none"
          zIndex={1000}
          style={{
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 55%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 55%)',
          }}
        />
      )}

      {/* Floating bottom nav */}
      {uiConfig && filteredNavButtons.length > 1 &&
        ((hasStrategyBeenCreated && !isCreatingStrategy && !isStrategyCreationMode) ||
          (shortSummary && finalProgramSequence?.length > 0)) && (
        <Box
          position="fixed"
          bottom={{ base: 4, md: 6 }}
          left="50%"
          zIndex={1001}
          borderRadius="full"
          px={{ base: 2, md: 3 }}
          py={{ base: 2, md: 3 }}
          style={{
            transform: navTransform,
            maxWidth: navMaxWidth,
            background: navBg,
            border: navBorder,
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <Box position="relative">

            <HStack spacing={1} position="relative" zIndex={1}>
              {/* Inline tabs */}
              {inlineTabs.map(({ viewKey, config, featureInfo }) => {
                const isActive = currentView === config.id && !showAddView;
                const tooltipDescription = featureInfo?.description || config.tooltip;
                const tooltipLabel = (
                  <Box>
                    <Text fontWeight="semibold" fontSize="15px">{config.label}</Text>
                    {tooltipDescription && (
                      <Text fontSize="11px" opacity={0.8}>{tooltipDescription}</Text>
                    )}
                  </Box>
                );
                return (
                  <Tooltip
                    key={viewKey}
                    label={tooltipLabel}
                    placement="top"
                    hasArrow
                    openDelay={50}
                    fontSize="12px"
                    borderRadius="md"
                    px={3}
                    py={2}
                  >
                    <HStack
                      as="button"
                      data-walkthrough-anchor={viewKey === "designAuthority" ? "design-authority" : undefined}
                      spacing={0}
                      onClick={() => {
                        setShowAddView(false);
                        startTransition(() => setCurrentView(config.id));
                      }}
                      px={{ base: 3, md: 4 }}
                      py={{ base: 2, md: 3 }}
                      borderRadius="full"
                      cursor="pointer"
                      border="none"
                      outline="none"
                      color={isActive ? navActiveTextColor : navMutedClr}
                      bg={isActive ? `rgba(${nr},${ng},${nb},1)` : 'transparent'}
                      _hover={{ bg: isActive ? `rgba(${nr},${ng},${nb},1)` : `rgba(${nr},${ng},${nb},0.08)` }}
                      style={{
                        gap: isNavShrunk ? '0px' : '8px',
                        transition: 'color 0.25s ease, background 0.2s ease, gap 0.3s cubic-bezier(0.4,0,0.2,1)',
                      }}
                    >
                      <Box as="span" className="material-icons-outlined" fontSize="18px" lineHeight="1">
                        {config.icon}
                      </Box>
                      <Text
                        fontSize="13px"
                        fontWeight={isActive ? 'semibold' : 'medium'}
                        whiteSpace="nowrap"
                        lineHeight="1"
                        style={{
                          maxWidth: isNavShrunk ? '0px' : '160px',
                          overflow: 'hidden',
                          opacity: isNavShrunk ? 0 : 1,
                          transition: 'max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease',
                        }}
                      >
                        {config.label}
                      </Text>
                    </HStack>
                  </Tooltip>
                );
              })}

              {/* "+" button when all tabs fit inline and not all features enabled */}
              {overflowTabs.length === 0 && !allFeaturesEnabled && (
                <Tooltip label="Add more features to your plan" hasArrow placement="top" openDelay={50}>
                  <HStack
                    as="button"
                    spacing={1}
                    onClick={() => {
                      setShowAddView(true);
                      startTransition(() => setCurrentView("newView"));
                    }}
                    px={{ base: 2, md: 3 }}
                    py={{ base: 2, md: 3 }}
                    borderRadius="full"
                    cursor="pointer"
                    border="none"
                    outline="none"
                    bg="transparent"
                    _hover={{ bg: `rgba(${nr},${ng},${nb},0.08)` }}
                    style={{
                      color: navMutedClr,
                      transition: 'color 0.2s ease',
                    }}
                  >
                    <Box as="span" className="material-icons-outlined" fontSize="20px" lineHeight="1">
                      add
                    </Box>
                  </HStack>
                </Tooltip>
              )}

              {/* "..." overflow button — only when there are overflow tabs */}
              {overflowTabs.length > 0 && (
              <Box position="relative" ref={overflowBtnRef}>
                  {/* Overflow dropdown — renders above the nav */}
                  {overflowOpen && (
                    <Box
                      position="absolute"
                      bottom="calc(100% + 10px)"
                      right={0}
                      borderRadius="xl"
                      overflow="hidden"
                      style={{
                        background: navBg,
                        border: navBorder,
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        minWidth: '160px',
                        animation: 'viewFadeIn 0.15s ease',
                      }}
                    >
                      <VStack spacing={0} align="stretch" py={1}>
                        {/* Overflow view tabs (plan sections that don't fit inline) */}
                        {overflowTabs.map(({ viewKey, config }) => {
                          const isActive = currentView === config.id && !showAddView;
                          return (
                            <HStack
                              key={viewKey}
                              as="button"
                              spacing={3}
                              onClick={() => {
                                setShowAddView(false);
                                startTransition(() => setCurrentView(config.id));
                                setOverflowOpen(false);
                              }}
                              px={4}
                              py={3}
                              cursor="pointer"
                              border="none"
                              outline="none"
                              w="100%"
                              _hover={{ filter: 'brightness(0.93)' }}
                              style={{
                                background: isActive ? `rgba(${nr},${ng},${nb},0.12)` : 'transparent',
                                color: isActive ? `rgba(${nr},${ng},${nb},1)` : navMutedClr,
                                transition: 'background 0.15s ease, color 0.15s ease',
                              }}
                            >
                              <Box as="span" className="material-icons-outlined" fontSize="18px" lineHeight="1">
                                {config.icon}
                              </Box>
                              <Text fontSize="13px" fontWeight={isActive ? 'semibold' : 'medium'} whiteSpace="nowrap" lineHeight="1">
                                {config.label}
                              </Text>
                            </HStack>
                          );
                        })}
                        {/* "More +" entry at bottom when not all features enabled */}
                        {!allFeaturesEnabled && (
                          <>
                            <Box borderTop="1px solid rgba(0,0,0,0.08)" mx={2} />
                            <HStack
                              as="button"
                              spacing={3}
                              onClick={() => {
                                setOverflowOpen(false);
                                setShowAddView(true);
                                startTransition(() => setCurrentView("newView"));
                              }}
                              px={4}
                              py={3}
                              cursor="pointer"
                              border="none"
                              outline="none"
                              w="100%"
                              _hover={{ filter: 'brightness(0.93)' }}
                              style={{
                                background: 'transparent',
                                color: `rgba(${nr},${ng},${nb},1)`,
                                transition: 'background 0.15s ease',
                              }}
                            >
                              <Box as="span" className="material-icons-outlined" fontSize="18px" lineHeight="1">
                                add
                              </Box>
                              <Text fontSize="13px" fontWeight="medium" whiteSpace="nowrap" lineHeight="1">
                                More +
                              </Text>
                            </HStack>
                          </>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* The dots button itself */}
                  <HStack
                    as="button"
                    spacing={1}
                    onClick={() => setOverflowOpen(o => !o)}
                    px={{ base: 2, md: 3 }}
                    py={{ base: 2, md: 3 }}
                    borderRadius="xl"
                    cursor="pointer"
                    border="none"
                    outline="none"
                    _hover={{ bg: overflowOpen ? undefined : `rgba(${nr},${ng},${nb},0.08)` }}
                    style={{
                      background: overflowOpen ? `rgba(${nr},${ng},${nb},0.12)` : 'transparent',
                      color: navMutedClr,
                      transition: 'background 0.2s ease',
                    }}
                  >
                    <Box as="span" className="material-icons-outlined" fontSize="20px" lineHeight="1">
                      more_horiz
                    </Box>
                  </HStack>
                </Box>
              )}
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PlanPage;
